import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, DollarSign, CheckCircle, X, MapPin, Users, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Cloud, Plus, Pencil, Check, Trash2, Video, Search, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useNylasAccounts } from '../hooks';
import { useUserId, useAuth } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';
import YearlyCalendarView from '../components/calendar/YearlyCalendarView';
import WeeklyCalendarView from '../components/calendar/WeeklyCalendarView';
import TodayCalendarView from '../components/calendar/TodayCalendarView';
import PermanentSyncKeyPanel from '../components/calendar/PermanentSyncKeyPanel';
import DatePickerPopover from '../components/DatePickerPopover';

// Get API URL from environment - defined at module level to ensure it's captured at build time
const API_URL = (() => {
  // Always use the current origin for API calls - runtime detection
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
})();

const RealEstateManagingPartnerCalendar = () => {
  // User ID and token from auth context - dynamic based on logged-in user
  const userId = useUserId();
  const { token: authToken } = useAuth();
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  const [loading, setLoading] = useState(false);
  const [activeCalendar, setActiveCalendar] = useState('business');
  const [activeCategory, setActiveCategory] = useState('all');
  // View state: 'month', 'week', or 'year'
  const [activeView, setActiveView] = useState('month');
  // Initialize to current month and year
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date()); // Default to today
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryModalType, setSummaryModalType] = useState('');
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [widgetModalType, setWidgetModalType] = useState('');
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState({ day: null, events: [] });
  
  // Local Calendar Events (stored in database for persistence)
  const [localEvents, setLocalEvents] = useState([]);
  const [loadingLocalEvents, setLoadingLocalEvents] = useState(true);
  
  // Drag-and-drop state for calendar events
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const handleEventDragStart = (e, event) => {
    if (event.source !== 'local') {
      e.preventDefault();
      return;
    }
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event.id);
    // Make the drag ghost semi-transparent
    if (e.target) {
      e.target.style.opacity = '0.5';
    }
  };

  const handleEventDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = '1';
    }
    setDraggedEvent(null);
    setDragOverTarget(null);
  };

  const handleDayDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(day);
  };

  const handleDayDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDayDrop = (e, targetDay) => {
    e.preventDefault();
    setDragOverTarget(null);
    if (!draggedEvent || draggedEvent.source !== 'local') return;

    // Calculate the new start_time and end_time preserving original time
    let hours = 9, minutes = 0, durationMs = 3600000;
    if (draggedEvent.start_time) {
      const oldStart = new Date(draggedEvent.start_time);
      hours = oldStart.getHours();
      minutes = oldStart.getMinutes();
      if (draggedEvent.end_time) {
        durationMs = new Date(draggedEvent.end_time) - oldStart;
      }
    }

    const newStart = new Date(currentYear, currentMonth, targetDay, hours, minutes);
    const newEnd = new Date(newStart.getTime() + durationMs);

    setLocalEvents(prev => prev.map(ev => {
      if (ev.id === draggedEvent.id) {
        return {
          ...ev,
          day: targetDay,
          month: currentMonth,
          year: currentYear,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      return ev;
    }));

    setDraggedEvent(null);
    toast.success(`Moved "${draggedEvent.title || draggedEvent.name}" to ${months[currentMonth]} ${targetDay}`);
    window.dispatchEvent(new CustomEvent('calendarUpdated'));
  };

  // Handler for week view drag-and-drop (passed as callback)
  const handleWeekEventDrop = (event, targetDate, targetHour, targetMinute = 0) => {
    if (!event || event.source !== 'local') return;

    let durationMs = 3600000;
    if (event.start_time && event.end_time) {
      durationMs = new Date(event.end_time) - new Date(event.start_time);
    }

    const newStart = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      targetHour, targetMinute
    );
    const newEnd = new Date(newStart.getTime() + durationMs);

    setLocalEvents(prev => prev.map(ev => {
      if (ev.id === event.id) {
        return {
          ...ev,
          day: newStart.getDate(),
          month: newStart.getMonth(),
          year: newStart.getFullYear(),
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      return ev;
    }));

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const timeStr = `${targetHour % 12 || 12}:${String(targetMinute).padStart(2, '0')} ${targetHour >= 12 ? 'PM' : 'AM'}`;
    toast.success(`Moved "${event.title || event.name}" to ${dayNames[targetDate.getDay()]} ${timeStr}`);
    window.dispatchEvent(new CustomEvent('calendarUpdated'));
  };
  
  // Fetch local events from database on mount
  useEffect(() => {
    const fetchLocalEvents = async () => {
      // Use demo data if in demo mode
      if (isDemoMode && demoData?.calendar_events) {
        const demoEvents = demoData.calendar_events.map(evt => ({
          id: evt.id,
          title: evt.title,
          start_time: evt.start_time,
          end_time: evt.end_time || new Date(new Date(evt.start_time).getTime() + 60*60*1000).toISOString(),
          location: evt.location || '',
          description: evt.client ? `Client: ${evt.client}` : '',
          category: evt.type || 'meeting',
          source: 'demo',
          color: evt.type === 'meeting' ? 'bg-blue-500' : 
                 evt.type === 'consultation' ? 'bg-green-500' : 
                 evt.type === 'session' ? 'bg-purple-500' : 'bg-gray-500'
        }));
        setLocalEvents(demoEvents);
        setLoadingLocalEvents(false);
        console.log(`📅 Demo mode: Loaded ${demoEvents.length} demo calendar events`);
        return;
      }
      
      try {
        setLoadingLocalEvents(true);
        // Per-user namespaced localStorage key — prevents one account's
        // calendar cache from leaking into another account on the same browser.
        const userKey = `local_calendar_events__${userId || 'anonymous'}`;
        const response = await fetch(`${API_URL}/api/local-calendar-events?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          setLocalEvents(data.events || []);
          // Also update localStorage for backward compatibility
          localStorage.setItem(userKey, JSON.stringify(data.events || []));
        } else {
          // Fallback to localStorage if API fails
          const saved = localStorage.getItem(userKey);
          if (saved) {
            setLocalEvents(JSON.parse(saved));
          }
        }
      } catch (error) {
        console.error('Error fetching local events:', error);
        // Fallback to localStorage
        const userKey = `local_calendar_events__${userId || 'anonymous'}`;
        const saved = localStorage.getItem(userKey);
        if (saved) {
          setLocalEvents(JSON.parse(saved));
        }
      } finally {
        setLoadingLocalEvents(false);
      }
    };
    fetchLocalEvents();
  }, [userId, isDemoMode, demoData]);
  
  // Save local events to both database and localStorage whenever they change
  useEffect(() => {
    if (loadingLocalEvents) return; // Don't save while loading
    
    const saveLocalEvents = async () => {
      try {
        // Save to localStorage for immediate access (per-user namespaced)
        const userKey = `local_calendar_events__${userId || 'anonymous'}`;
        localStorage.setItem(userKey, JSON.stringify(localEvents));
        
        // Save to database for persistence
        await fetch(`${API_URL}/api/local-calendar-events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, events: localEvents })
        });
      } catch (error) {
        console.error('Error saving local events:', error);
      }
    };
    
    saveLocalEvents();
  }, [localEvents, loadingLocalEvents, userId]);
  
  // Outlook Desktop Sync State
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [outlookSyncStatus, setOutlookSyncStatus] = useState({ synced: false, last_sync: null, event_count: 0 });
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  const [showOutlookEvents, setShowOutlookEvents] = useState(true);
  
  // Use the Nylas accounts hook for unified account management (refactored)
  // Using hideAccount for page-specific tab removal (doesn't affect other pages)
  const { 
    accounts: nylasAccounts, 
    loading: loadingNylasAccounts,
    hideAccount: hideCalendarAccountTab,
    fetchAccounts: refetchNylasAccounts 
  } = useNylasAccounts(userId, 'calendar');
  
  // Nylas Calendar State (auto-sync from connected email accounts)
  const [nylasEvents, setNylasEvents] = useState([]);
  const [syncingNylas, setSyncingNylas] = useState(false);
  const [showNylasEvents, setShowNylasEvents] = useState(true);
  
  // Active calendar account tab (for filtering by account) - default to 'all' to show all calendars combined
  const [activeAccountTab, setActiveAccountTab] = useState('all'); // 'all', 'local-work', 'local-personal', or account email
  
  // Calendar Connection State
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [showICloudCalendarModal, setShowICloudCalendarModal] = useState(false);
  const [showGoogleCalendarModal, setShowGoogleCalendarModal] = useState(false);
  const [showOutlookCalendarModal, setShowOutlookCalendarModal] = useState(false);
  const [icloudCalEmail, setIcloudCalEmail] = useState('');
  const [icloudCalPassword, setIcloudCalPassword] = useState('');
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [connectedCalendarType, setConnectedCalendarType] = useState(null);
  
  // Add Event State
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventStartTime, setNewEventStartTime] = useState('09:00');
  const [newEventEndTime, setNewEventEndTime] = useState('10:00');
  const [newEventAllDay, setNewEventAllDay] = useState(false);
  const [newEventCategory, setNewEventCategory] = useState('other');
  const [newEventCalendarType, setNewEventCalendarType] = useState('work'); // 'work' or 'personal'
  // Repeat: 'none' | 'weekly' | 'monthly'
  const [newEventRepeat, setNewEventRepeat] = useState('none');
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventError, setCreateEventError] = useState('');
  
  // Collapse state for accounts container - collapsed by default
  const [isAccountsCollapsed, setIsAccountsCollapsed] = useState(true);
  
  // Edit Event State
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editEventData, setEditEventData] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    allDay: false,
    category: 'other',
    recurrence: 'none', // 'none' | 'weekly' | 'monthly'
  });
  
  // Editable Category State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('📅');
  const [newCategoryColor, setNewCategoryColor] = useState('blue');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');

  // ---------- Per-user category keyword rules + re-scan ----------
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [categoryKeywords, setCategoryKeywords] = useState({}); // { catId: [kw, kw] }
  const [keywordInputs, setKeywordInputs] = useState({});       // controlled inputs
  const [savingKeywords, setSavingKeywords] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  // Update individual event category (for synced Outlook/Calendar.app events)
  const [updatingEventCategory, setUpdatingEventCategory] = useState(false);

  // Claim legacy/orphan events (one-time backfill)
  const [claimingOrphans, setClaimingOrphans] = useState(false);

  // Inline-edit Location & Online-Meeting URL on synced events
  const [editingField, setEditingField] = useState(null); // 'location' | 'online_meeting_url' | null
  const [editFieldValue, setEditFieldValue] = useState('');
  const [savingEventField, setSavingEventField] = useState(false);

  // Reset inline edit state whenever a different event is opened
  useEffect(() => {
    setEditingField(null);
    setEditFieldValue('');
  }, [selectedEvent?.id]);
  
  // Default calendar categories
  const defaultCalendarCategories = [
    { id: 'all', label: 'All', color: 'gray', icon: '📋' },
    { id: 'meeting', label: 'Meetings', color: 'blue', icon: '👥' },
    { id: 'work', label: 'Work', color: 'blue', icon: '💼' },
    { id: 'birthday', label: 'Birthdays', color: 'pink', icon: '🎂' },
    { id: 'health', label: 'Health', color: 'red', icon: '⚕️' },
    { id: 'sports', label: 'Sports', color: 'orange', icon: '⚽' },
    { id: 'religious', label: 'Religious', color: 'purple', icon: '✝️' },
    { id: 'travel', label: 'Travel', color: 'cyan', icon: '✈️' },
    { id: 'home', label: 'Home', color: 'green', icon: '🏠' },
    { id: 'social', label: 'Social', color: 'yellow', icon: '🎉' },
    { id: 'education', label: 'Education', color: 'indigo', icon: '🎓' },
    { id: 'networking', label: 'Networking', color: 'teal', icon: '🤝' },
    { id: 'other', label: 'Other', color: 'gray', icon: '📁' }
  ];
  
  // Load custom categories from database (with localStorage fallback)
  const [customCalendarCategories, setCustomCalendarCategories] = useState(defaultCalendarCategories);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Fetch categories from database on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await fetch(`${API_URL}/api/user-profile/settings/calendar_categories?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.value && Array.isArray(data.value) && data.value.length > 0) {
            setCustomCalendarCategories(data.value);
            localStorage.setItem('calendar_categories', JSON.stringify(data.value));
          }
        } else {
          // Fallback to localStorage
          const saved = localStorage.getItem('calendar_categories');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setCustomCalendarCategories(parsed);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching calendar categories:', error);
        // Fallback to localStorage
        const saved = localStorage.getItem('calendar_categories');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setCustomCalendarCategories(parsed);
            }
          } catch { /* ignored */ }
        }
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [userId]);
  
  // Save categories to database whenever they change
  const saveCategoriesToDatabase = async (categories) => {
    try {
      // Save to localStorage for immediate access
      localStorage.setItem('calendar_categories', JSON.stringify(categories));
      
      // Save to database for persistence across deployments
      const response = await fetch(`${API_URL}/api/user-profile/settings/calendar_categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          key: 'calendar_categories',
          value: categories, 
          user_id: userId 
        })
      });
      
      if (response.ok) {
        console.log('📅 Saved calendar categories to database');
      } else {
        console.error('Failed to save categories to database');
      }
    } catch (error) {
      console.error('Error saving calendar categories:', error);
    }
  };

  // ---------- Custom keyword rules: load from backend on mount ----------
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/outlook-calendar/category-keywords?user_id=${encodeURIComponent(userId)}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j.rules && typeof j.rules === 'object') {
          setCategoryKeywords(j.rules);
        }
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const saveCategoryKeywords = async () => {
    if (!userId) return;
    setSavingKeywords(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/category-keywords`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, rules: categoryKeywords }),
      });
      if (r.ok) {
        toast.success('Keywords saved');
      } else {
        toast.error('Failed to save keywords');
      }
    } catch (e) {
      toast.error('Failed to save keywords');
    } finally {
      setSavingKeywords(false);
    }
  };

  const claimOrphanEvents = async () => {
    if (!userId) return;
    if (!window.confirm("This will assign your user_id to any calendar events that don't have one yet (legacy data from before user_id stamping). Continue?")) return;
    setClaimingOrphans(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/claim-orphan-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, user_email: null }),
      });
      if (r.ok) {
        const j = await r.json();
        toast.success(`Claimed ${j.modified} of ${j.matched} legacy events`);
        if (typeof window !== 'undefined') setTimeout(() => window.location.reload(), 800);
      } else {
        toast.error('Failed to claim legacy events');
      }
    } catch (e) {
      toast.error('Failed to claim legacy events');
    } finally {
      setClaimingOrphans(false);
    }
  };

  // Backfill Teams / Zoom / Meet URLs onto stored events that don't have one.
  // The Mac sync now extracts these from event body/location, but events
  // synced before that logic was added still have `online_meeting_url = null`.
  // This scans server-side and fills them in without waiting for re-sync.
  const [extractingLinks, setExtractingLinks] = useState(false);
  const extractMeetingLinks = async () => {
    if (!userId) return;
    setExtractingLinks(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/extract-meeting-links?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
      });
      if (r.ok) {
        const j = await r.json();
        if (j.updated > 0) {
          toast.success(`Extracted ${j.updated} meeting link${j.updated === 1 ? '' : 's'} from ${j.scanned} events`);
          if (typeof window !== 'undefined') setTimeout(() => window.location.reload(), 800);
        } else {
          toast.info(`Scanned ${j.scanned} events — no new links found (URL must be in event body, location, or web link).`);
        }
      } else {
        toast.error('Failed to extract meeting links');
      }
    } catch (e) {
      toast.error('Failed to extract meeting links');
    } finally {
      setExtractingLinks(false);
    }
  };

  // De-duplicate calendar events — removes orphan rows left behind when
  // an event was renamed in Outlook (old subject+start vs new subject+start
  // produced two rows). Keeps the most recently synced of each duplicate group.
  const [deduping, setDeduping] = useState(false);
  const dedupeEvents = async () => {
    if (!userId) return;
    setDeduping(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/dedupe-events?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
      });
      if (r.ok) {
        const j = await r.json();
        if (j.deleted > 0) {
          toast.success(`Removed ${j.deleted} duplicate${j.deleted === 1 ? '' : 's'} from ${j.duplicate_groups} group${j.duplicate_groups === 1 ? '' : 's'}`);
          if (typeof window !== 'undefined') setTimeout(() => window.location.reload(), 800);
        } else {
          toast.info(`Scanned ${j.scanned} events — no duplicates found.`);
        }
      } else {
        toast.error('Failed to dedupe events');
      }
    } catch (e) {
      toast.error('Failed to dedupe events');
    } finally {
      setDeduping(false);
    }
  };


  const rescanAllEvents = async (force = false) => {
    if (!userId) return;
    if (force) {
      const ok = window.confirm("This will WIPE all your manual category overrides and re-categorize every event from scratch using your keywords. Continue?");
      if (!ok) return;
    }
    setRescanning(true);
    try {
      // Save first so re-scan uses latest keywords
      await saveCategoryKeywords();
      const url = `${API_URL}/api/outlook-calendar/recategorize?user_id=${encodeURIComponent(userId)}${force ? '&force=true' : ''}`;
      const r = await fetch(url, { method: 'POST' });
      if (r.ok) {
        const j = await r.json();
        const msg = force
          ? `Force re-scanned ${j.total} events. Re-categorized ${j.updated}.`
          : `Re-scanned ${j.total} events. Updated ${j.updated}${j.skipped_overridden ? ` (kept ${j.skipped_overridden} manual overrides)` : ''}.`;
        toast.success(msg);
        // Refresh event list — reload page-level data
        if (typeof window !== 'undefined') window.location.reload();
      } else {
        toast.error('Failed to re-scan');
      }
    } catch (e) {
      toast.error('Failed to re-scan');
    } finally {
      setRescanning(false);
    }
  };

  // Delete a synced event — removes the row locally AND queues an Outlook delete
  const [deletingEvent, setDeletingEvent] = useState(false);
  const deleteSyncedEvent = async (eventId) => {
    if (!eventId || !userId) return;
    if (!window.confirm('Delete this event? It will also be removed from Outlook on the next sync.')) return;
    setDeletingEvent(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/event/${encodeURIComponent(eventId)}?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      if (r.ok) {
        setOutlookEvents(prev => prev.filter(ev => ev.id !== eventId));
        setSelectedEvent(null);
        toast.success('Event deleted (syncing to Outlook…)');
      } else {
        const t = await r.text().catch(() => '');
        toast.error(`Failed to delete (${r.status}): ${t.slice(0, 100)}`);
      }
    } catch (e) {
      toast.error('Failed to delete event: ' + (e?.message || ''));
    } finally {
      setDeletingEvent(false);
    }
  };

  const updateSyncedEventCategory = async (eventId, newCategoryId) => {
    if (!eventId || !newCategoryId) return;
    setUpdatingEventCategory(true);
    try {
      const r = await fetch(`${API_URL}/api/outlook-calendar/event/${encodeURIComponent(eventId)}/category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: newCategoryId, user_id: userId }),
      });
      if (r.ok) {
        // Optimistically update the modal-displayed event + main events list
        setSelectedEvent(prev => prev ? { ...prev, category: newCategoryId, color: getCategoryColor(newCategoryId), category_overridden: true } : prev);
        setOutlookEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, category: newCategoryId, color: getCategoryColor(newCategoryId), category_overridden: true } : ev));
        toast.success('Category updated');
      } else {
        const txt = await r.text().catch(() => '');
        console.error('Update category failed:', r.status, txt);
        toast.error(`Failed to update category (${r.status})`);
      }
    } catch (e) {
      console.error('Update category exception:', e);
      toast.error('Failed to update category: ' + (e?.message || ''));
    } finally {
      setUpdatingEventCategory(false);
    }
  };

  // Patch a single editable field (location / online_meeting_url) on a synced event
  const saveEventField = async (eventId, field, value) => {
    if (!eventId || !field) return;
    setSavingEventField(true);
    try {
      const body = { user_id: userId };
      body[field] = value || '';
      const r = await fetch(`${API_URL}/api/outlook-calendar/event/${encodeURIComponent(eventId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const newVal = field === 'online_meeting_url' ? (value.trim() || null) : value;
        setSelectedEvent(prev => prev ? { ...prev, [field]: newVal } : prev);
        setOutlookEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, [field]: newVal } : ev));
        toast.success(field === 'online_meeting_url' ? 'Meeting link saved' : 'Location saved');
        setEditingField(null);
      } else if (r.status === 404) {
        toast.error('Event not found in DB. Try the "Claim legacy events" button below, or redeploy if you just edited categories.');
      } else {
        const txt = await r.text().catch(() => '');
        toast.error(`Failed to save (${r.status}). ${txt.slice(0, 100)}`);
      }
    } catch (e) {
      toast.error('Failed to save: ' + (e.message || ''));
    } finally {
      setSavingEventField(false);
    }
  };
  
  // Available emoji icons for calendar categories
  const calendarCategoryIcons = ['📅', '📋', '👥', '💼', '🏢', '🎂', '⚕️', '⚽', '✝️', '✈️', '🏠', '🎉', '🎓', '🤝', '📁', '🎯', '🌟', '💡', '🔔', '⏰', '🎵', '🎨', '📊', '💰', '🛒', '🍽️', '🎮', '🏋️', '📱', '💻'];
  
  // Available colors for categories
  const categoryColors = ['blue', 'emerald', 'pink', 'red', 'orange', 'purple', 'cyan', 'green', 'yellow', 'indigo', 'teal', 'gray'];
  
  // Delete calendar account confirmation state
  const [showDeleteCalendarConfirm, setShowDeleteCalendarConfirm] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  const [deletingCalendar, setDeletingCalendar] = useState(false);
  
  // Check if any calendar is connected on mount
  useEffect(() => {
    const storedCalType = localStorage.getItem('calendar_connected_type');
    if (storedCalType) {
      setIsCalendarConnected(true);
      setConnectedCalendarType(storedCalType);
    }
    
    // Check for Nylas callback URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle Nylas success callback
    if (urlParams.get('nylas_connected') === 'true') {
      const email = urlParams.get('email');
      const provider = urlParams.get('provider');
      toast.success(`Connected ${email || 'calendar'} successfully!`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle Nylas error callback
    if (urlParams.get('nylas_error') === 'true') {
      const message = urlParams.get('message');
      toast.error(message || 'Failed to connect calendar');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Get API URL - use module-level API_URL constant
  const getApiUrl = () => {
    return API_URL;
  };
  const apiUrl = getApiUrl();
  
  // Fetch Outlook desktop calendar events
  const fetchOutlookEvents = async () => {
    try {
      setSyncingOutlook(true);
      console.log('Fetching Outlook events for month:', currentMonth + 1, currentYear);
      const userQuery = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-events-by-month/${currentYear}/${currentMonth + 1}${userQuery}`);
      const data = await response.json();
      console.log('Outlook events response:', data);
      
      if (data.status === 'success' && data.events_by_day) {
        // Helper to render an ISO start as a 12-hour clock string
        const fmtTime = (iso) => {
          if (!iso) return '';
          try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          } catch { return ''; }
        };
        // Convert events_by_day to flat array with day numbers
        const events = [];
        Object.entries(data.events_by_day).forEach(([day, dayEvents]) => {
          dayEvents.forEach(event => {
            const isAllDay = !!event.is_all_day;
            events.push({
              ...event,
              day: parseInt(day),
              name: event.subject || event.title,
              source: 'outlook',
              // Normalize time fields so the modal, calendar squares,
              // weekly view, and yearly view all show a real clock time
              // (instead of falling through to "All Day").
              start_time: event.start || null,
              end_time: event.end || null,
              time: isAllDay ? 'All Day' : fmtTime(event.start),
              all_day: isAllDay,
            });
          });
        });
        setOutlookEvents(events);
        setOutlookSyncStatus({
          synced: true,
          last_sync: data.synced_at,
          event_count: data.total_events
        });
      } else {
        setOutlookEvents([]);
        setOutlookSyncStatus({ synced: false, last_sync: null, event_count: 0 });
      }
    } catch (error) {
      console.error('Error fetching Outlook events:', error);
      setOutlookEvents([]);
    } finally {
      setSyncingOutlook(false);
    }
  };
  
  // Fetch Nylas calendar events for all connected accounts (refactored: uses useNylasAccounts hook)
  const fetchNylasEvents = async () => {
    try {
      setSyncingNylas(true);
      
      // Use accounts from the hook (no need to fetch again)
      if (nylasAccounts.length === 0) {
        console.log('📅 No Nylas accounts connected');
        setNylasEvents([]);
        return;
      }
      
      // Build auth headers
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      // Fetch events for each account
      const allEvents = [];
      for (const account of nylasAccounts) {
        try {
          console.log(`📅 Fetching Nylas events for ${account.email}, month: ${currentMonth + 1}/${currentYear}`);
          const tokenParam = authToken ? `token=${authToken}` : `user_id=${userId}`;
          const response = await fetch(
            `${apiUrl}/api/nylas/events/by-month/${currentYear}/${currentMonth + 1}?${tokenParam}&email=${encodeURIComponent(account.email)}`,
            { headers }
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success' && data.events_by_day) {
              // Convert events_by_day to flat array with day numbers
              Object.entries(data.events_by_day).forEach(([day, dayEvents]) => {
                dayEvents.forEach(event => {
                  allEvents.push({
                    ...event,
                    day: parseInt(day),
                    name: event.title,
                    source: 'nylas',
                    account_email: account.email
                  });
                });
              });
              console.log(`📅 Fetched ${data.total_events} events for ${account.email}`);
            }
          }
        } catch (err) {
          console.error(`Error fetching events for ${account.email}:`, err);
        }
      }
      
      setNylasEvents(allEvents);
      console.log(`📅 Total Nylas events: ${allEvents.length}`);
      
    } catch (error) {
      console.error('Error fetching Nylas events:', error);
      setNylasEvents([]);
    } finally {
      setSyncingNylas(false);
    }
  };
  
  // Handle deleting/disconnecting a calendar account
  const handleDeleteCalendar = (account) => {
    setCalendarToDelete(account);
    setShowDeleteCalendarConfirm(true);
  };
  
  // Confirm and execute calendar tab removal (page-specific, doesn't affect other pages)
  const confirmDeleteCalendar = async () => {
    if (!calendarToDelete) return;
    
    try {
      setDeletingCalendar(true);
      
      // Use hideAccount to only hide on THIS page (Calendar)
      // This does NOT disconnect from Nylas API, so Contacts and Email pages keep the account
      const identifier = calendarToDelete.grant_id || calendarToDelete.email;
      hideCalendarAccountTab(identifier);
      
      // Remove events for this account from local state
      setNylasEvents(prev => prev.filter(e => e.account_email !== calendarToDelete.email));
      
      // Switch to 'all' tab if this was the active tab
      if (activeAccountTab === calendarToDelete.email) {
        setActiveAccountTab('all');
      }
      
      toast.success(`Removed ${calendarToDelete.account_name || calendarToDelete.email} from Calendar`);
    } catch (error) {
      console.error('Error removing calendar tab:', error);
      toast.error('Failed to remove calendar tab');
    } finally {
      setDeletingCalendar(false);
      setShowDeleteCalendarConfirm(false);
      setCalendarToDelete(null);
    }
  };
  
  // Connect new calendar via Nylas
  const handleConnectCalendar = () => {
    const accountName = prompt('Enter a name for this calendar account (e.g., "Work Calendar", "Personal"):');
    if (accountName) {
      window.location.href = `${apiUrl}/api/nylas/auth/connect?user_id=${userId}&account_name=${encodeURIComponent(accountName)}&redirect_to=calendar&redirect_host=${encodeURIComponent(window.location.origin)}`;
    }
  };
  
  // Check Outlook sync status on mount
  const checkOutlookSyncStatus = async () => {
    try {
      console.log('Checking Outlook sync status...');
      const userQuery = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-sync-status${userQuery}`);
      const data = await response.json();
      console.log('Sync status:', data);
      setOutlookSyncStatus(data);
      if (data.synced) {
        fetchOutlookEvents();
      }
    } catch (error) {
      console.error('Error checking Outlook sync status:', error);
    }
  };
  
  // Handle iCloud Calendar connection
  const handleICloudCalendarConnect = async () => {
    if (!icloudCalEmail || !icloudCalPassword) {
      setCalendarError('Please enter both email and password');
      return;
    }
    
    setCalendarConnecting(true);
    setCalendarError('');
    
    try {
      const response = await fetch(`${apiUrl}/api/icloud-calendar/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apple_id: icloudCalEmail,
          app_password: icloudCalPassword
        })
      });
      
      if (response.ok) {
        setIsCalendarConnected(true);
        setConnectedCalendarType('icloud');
        localStorage.setItem('calendar_connected_type', 'icloud');
        localStorage.setItem('icloud_cal_email', icloudCalEmail);
        localStorage.setItem('icloud_cal_password', icloudCalPassword);
        setShowICloudCalendarModal(false);
        // Refresh calendar events
        fetchOutlookEvents();
      } else {
        const data = await response.json();
        setCalendarError(data.detail || 'Failed to connect. Please check your credentials.');
      }
    } catch (error) {
      setCalendarError('Connection failed. Please try again.');
    } finally {
      setCalendarConnecting(false);
    }
  };
  
  // Handle Google Calendar connect (show modal)
  const handleGoogleCalendarConnect = () => {
    setShowGoogleCalendarModal(true);
  };
  
  // Handle Outlook Calendar connect (show modal)
  const handleOutlookCalendarConnect = () => {
    setShowOutlookCalendarModal(true);
  };
  
  // Actually connect Outlook (from modal)
  const connectOutlookCalendar = () => {
    // Trigger existing Outlook sync
    fetchOutlookEvents();
    setIsCalendarConnected(true);
    setConnectedCalendarType('outlook');
    localStorage.setItem('calendar_connected_type', 'outlook');
    setShowOutlookCalendarModal(false);
  };
  
  // Create a new event - either locally or via Nylas API
  const createNewEvent = async () => {
    if (!newEventTitle.trim()) {
      setCreateEventError('Please enter an event title');
      return;
    }
    if (!newEventDate) {
      setCreateEventError('Please select a date');
      return;
    }
    
    setCreatingEvent(true);
    setCreateEventError('');
    
    // Determine if we should save locally or via Nylas.
    // ALWAYS save locally when the "All Calendars", "Work" or "Personal" tab is active, so the user can add
    // a local event without having to switch tabs first.
    const shouldSaveLocally =
      activeAccountTab === 'local-work' ||
      activeAccountTab === 'local-personal' ||
      activeAccountTab === 'all' ||
      nylasAccounts.length === 0;
    
    if (shouldSaveLocally) {
      try {
        // Parse date components directly from the date string to avoid timezone issues
        // newEventDate is in format "YYYY-MM-DD"
        const [yearStr, monthStr, dayStr] = newEventDate.split('-');
        const eventYear = parseInt(yearStr, 10);
        const eventMonth = parseInt(monthStr, 10) - 1; // Convert to 0-indexed
        const eventDay = parseInt(dayStr, 10);
        
        // Create start/end times using local timezone
        const startDateTime = new Date(eventYear, eventMonth, eventDay, 
          parseInt(newEventStartTime.split(':')[0], 10), 
          parseInt(newEventStartTime.split(':')[1], 10));
        const endDateTime = new Date(eventYear, eventMonth, eventDay,
          parseInt(newEventEndTime.split(':')[0], 10),
          parseInt(newEventEndTime.split(':')[1], 10));
        
        const newLocalEvent = {
          id: `local-${Date.now()}`,
          title: newEventTitle,
          description: newEventDescription,
          location: newEventLocation,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          all_day: newEventAllDay,
          day: eventDay,
          month: eventMonth,
          year: eventYear,
          source: 'local',
          category: newEventCategory,
          calendar_type: newEventCalendarType, // 'work' or 'personal'
          color: getCategoryColor(newEventCategory),
          recurrence: newEventRepeat || 'none',
          created_at: new Date().toISOString()
        };
        
        setLocalEvents(prev => [...prev, newLocalEvent]);
        setShowAddEventModal(false);
        resetEventForm();
        toast.success('Event added to Local Calendar');
        
        // Notify dashboard to refresh
        window.dispatchEvent(new CustomEvent('calendarUpdated'));
        // Also update localStorage timestamp to trigger cross-tab updates
        localStorage.setItem('calendar_events_updated', Date.now().toString());
      } catch (error) {
        console.error('Error creating local event:', error);
        setCreateEventError('Failed to create event');
      } finally {
        setCreatingEvent(false);
      }
      return;
    }
    
    // Otherwise, create via Nylas API
    try {
      // Build auth headers
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
      };
      
      // First, get the primary calendar ID
      const calendarsResponse = await fetch(
        `${apiUrl}/api/nylas/calendars${authToken ? `?token=${authToken}` : `?user_id=${userId}`}`,
        { headers }
      );
      const calendarsData = await calendarsResponse.json();
      
      if (!calendarsData.calendars || calendarsData.calendars.length === 0) {
        setCreateEventError('No calendars found. Please connect a calendar first.');
        return;
      }
      
      // Find the primary calendar or use the first writable one
      const primaryCalendar = calendarsData.calendars.find(c => c.is_primary && !c.read_only) 
        || calendarsData.calendars.find(c => !c.read_only);
      
      if (!primaryCalendar) {
        setCreateEventError('No writable calendar found. Please check your calendar permissions.');
        return;
      }
      
      // Build start and end timestamps - parse date correctly to avoid timezone issues
      const [yearStr, monthStr, dayStr] = newEventDate.split('-');
      const eventYear = parseInt(yearStr, 10);
      const eventMonth = parseInt(monthStr, 10) - 1;
      const eventDay = parseInt(dayStr, 10);
      
      const startDateTime = new Date(eventYear, eventMonth, eventDay,
        parseInt(newEventStartTime.split(':')[0], 10),
        parseInt(newEventStartTime.split(':')[1], 10));
      const endDateTime = new Date(eventYear, eventMonth, eventDay,
        parseInt(newEventEndTime.split(':')[0], 10),
        parseInt(newEventEndTime.split(':')[1], 10));
      
      const eventPayload = {
        title: newEventTitle,
        description: newEventDescription,
        location: newEventLocation,
        start_time: Math.floor(startDateTime.getTime() / 1000).toString(),
        end_time: Math.floor(endDateTime.getTime() / 1000).toString(),
        all_day: newEventAllDay,
        calendar_id: primaryCalendar.id
      };
      
      const response = await fetch(
        `${apiUrl}/api/nylas/events${authToken ? `?token=${authToken}` : `?user_id=${userId}`}`, 
        {
          method: 'POST',
          headers,
          body: JSON.stringify(eventPayload)
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.status === 'created') {
        // Success - close modal and refresh events
        setShowAddEventModal(false);
        resetEventForm();
        // Refresh events
        fetchNylasEvents();
        fetchOutlookEvents();
      } else {
        setCreateEventError(data.detail || 'Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setCreateEventError('Failed to create event. Please try again.');
    } finally {
      setCreatingEvent(false);
    }
  };
  
  // Delete a local event
  const deleteLocalEvent = (eventId) => {
    setLocalEvents(prev => prev.filter(e => e.id !== eventId));
    toast.success('Event deleted');
  };
  
  // Reset the add event form
  const resetEventForm = () => {
    setNewEventTitle('');
    setNewEventDescription('');
    setNewEventLocation('');
    setNewEventDate('');
    setNewEventStartTime('09:00');
    setNewEventEndTime('10:00');
    setNewEventAllDay(false);
    setNewEventCategory('other');
    setNewEventCalendarType('work');
    setNewEventRepeat('none');
    setCreateEventError('');
  };
  
  // Start editing an event
  const startEditingEvent = (event) => {
    let startTime = '09:00';
    let endTime = '10:00';
    let dateStr = '';

    // Priority 1: build date from day/month/year — these are what the calendar squares use,
    // so the edit form must always show the same date the user sees on the grid.
    if (event.day && event.year) {
      // event.month may be a number (0-indexed) stored in localEvents,
      // or a string like "April" stored in selectedEvent after an edit.
      let monthIdx;
      if (typeof event.month === 'string') {
        monthIdx = months.indexOf(event.month);
        if (monthIdx === -1) monthIdx = currentMonth;
      } else if (typeof event.month === 'number') {
        monthIdx = event.month;
      } else {
        monthIdx = currentMonth;
      }
      dateStr = `${event.year}-${String(monthIdx + 1).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`;
    }

    // Extract time from start_time; also fall back to deriving date if day/month/year missing
    if (event.start_time) {
      const startDate = typeof event.start_time === 'number'
        ? new Date(event.start_time * 1000)
        : new Date(event.start_time);
      startTime = startDate.toTimeString().slice(0, 5);
      if (!dateStr) {
        dateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      }
    }

    if (event.end_time) {
      const endDate = typeof event.end_time === 'number'
        ? new Date(event.end_time * 1000)
        : new Date(event.end_time);
      endTime = endDate.toTimeString().slice(0, 5);
    }

    // Final fallback — should never hit this
    if (!dateStr) {
      const t = new Date();
      dateStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    }
    
    setEditEventData({
      title: event.title || event.name || '',
      description: event.description || '',
      location: event.location || '',
      date: dateStr,
      startTime: startTime,
      endTime: endTime,
      allDay: event.all_day || false,
      category: event.category || 'other',
      recurrence: event.recurrence || 'none',
    });
    setIsEditingEvent(true);
  };
  
  // Save edited event
  const saveEditedEvent = () => {
    if (!selectedEvent) return;
    
    // Only local events can be edited for now
    if (selectedEvent.source !== 'local') {
      toast.error('Only local calendar events can be edited');
      return;
    }
    
    if (!editEventData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    
    // Parse date string directly to avoid UTC midnight timezone shift
    const [yearStr, monthStr, dayStr] = editEventData.date.split('-');
    const eventYear  = parseInt(yearStr,  10);
    const eventMonth = parseInt(monthStr, 10) - 1; // 0-indexed
    const eventDay   = parseInt(dayStr,   10);
    const startDateTime = new Date(eventYear, eventMonth, eventDay,
      parseInt(editEventData.startTime.split(':')[0], 10),
      parseInt(editEventData.startTime.split(':')[1], 10));
    const endDateTime = new Date(eventYear, eventMonth, eventDay,
      parseInt(editEventData.endTime.split(':')[0], 10),
      parseInt(editEventData.endTime.split(':')[1], 10));

    // Re-derive day/month/year from the constructed local Date to guarantee
    // they always match what the calendar will render from start_time.
    const savedDay   = startDateTime.getDate();
    const savedMonth = startDateTime.getMonth();
    const savedYear  = startDateTime.getFullYear();
    
    setLocalEvents(prev => prev.map(event => {
      if (event.id === selectedEvent.id) {
        return {
          ...event,
          title: editEventData.title,
          description: editEventData.description,
          location: editEventData.location,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          all_day: editEventData.allDay,
          day: savedDay,
          month: savedMonth,
          year: savedYear,
          category: editEventData.category,
          color: getCategoryColor(editEventData.category),
          recurrence: editEventData.recurrence || 'none',
          updated_at: new Date().toISOString()
        };
      }
      return event;
    }));
    
    // Update the selected event to reflect changes
    setSelectedEvent(prev => ({
      ...prev,
      title: editEventData.title,
      name: editEventData.title,
      description: editEventData.description,
      location: editEventData.location,
      day: savedDay,
      month: months[savedMonth],
      year: savedYear,
      category: editEventData.category
    }));
    
    setIsEditingEvent(false);
    toast.success('Event updated successfully');
    
    // Notify dashboard to refresh
    window.dispatchEvent(new CustomEvent('calendarUpdated'));
    localStorage.setItem('calendar_events_updated', Date.now().toString());
  };
  
  // Delete an event
  const deleteEvent = () => {
    if (!selectedEvent) return;
    
    // Only local events can be deleted for now
    if (selectedEvent.source !== 'local') {
      toast.error('Only local calendar events can be deleted');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this event?')) {
      setLocalEvents(prev => prev.filter(event => event.id !== selectedEvent.id));
      setShowEventModal(false);
      setSelectedEvent(null);
      toast.success('Event deleted successfully');
      
      // Notify dashboard to refresh
      window.dispatchEvent(new CustomEvent('calendarUpdated'));
      localStorage.setItem('calendar_events_updated', Date.now().toString());
    }
  };
  
  // Duplicate an event - pre-fills the Add Event modal with the selected event's data
  const duplicateEvent = () => {
    if (!selectedEvent) return;
    
    // Only local events can be duplicated
    if (selectedEvent.source !== 'local') {
      toast.error('Only local calendar events can be duplicated');
      return;
    }
    
    // Extract date from the selected event
    let dateStr = '';
    if (selectedEvent.day && selectedEvent.year) {
      let monthIdx;
      if (typeof selectedEvent.month === 'string') {
        monthIdx = months.indexOf(selectedEvent.month);
        if (monthIdx === -1) monthIdx = currentMonth;
      } else if (typeof selectedEvent.month === 'number') {
        monthIdx = selectedEvent.month;
      } else {
        monthIdx = currentMonth;
      }
      dateStr = `${selectedEvent.year}-${String(monthIdx + 1).padStart(2, '0')}-${String(selectedEvent.day).padStart(2, '0')}`;
    }
    
    // Extract times from start_time and end_time
    let startTime = '09:00';
    let endTime = '10:00';
    if (selectedEvent.start_time) {
      const startDate = new Date(selectedEvent.start_time);
      startTime = startDate.toTimeString().slice(0, 5);
      if (!dateStr) {
        dateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      }
    }
    if (selectedEvent.end_time) {
      const endDate = new Date(selectedEvent.end_time);
      endTime = endDate.toTimeString().slice(0, 5);
    }
    
    // Pre-fill the Add Event modal with the selected event's data
    setNewEventTitle(`${selectedEvent.title || selectedEvent.name || ''} (Copy)`);
    setNewEventDescription(selectedEvent.description || '');
    setNewEventLocation(selectedEvent.location || '');
    setNewEventDate(dateStr);
    setNewEventStartTime(startTime);
    setNewEventEndTime(endTime);
    setNewEventAllDay(selectedEvent.all_day || false);
    setNewEventCategory(selectedEvent.category || 'other');
    setNewEventCalendarType(selectedEvent.calendar_type || 'work');
    setNewEventRepeat(selectedEvent.recurrence || 'none');
    setCreateEventError('');
    
    // Close the event details modal and open the Add Event modal
    setShowEventModal(false);
    setSelectedEvent(null);
    setShowAddEventModal(true);
    
    toast.info('Edit the details and save to create a duplicate event');
  };
  
  // Cancel editing
  const cancelEditingEvent = () => {
    setIsEditingEvent(false);
  };
  
  // Open add event modal with selected date
  const openAddEventModal = (date = null) => {
    if (date) {
      // Format date as YYYY-MM-DD
      const year = currentYear;
      const month = String(currentMonth + 1).padStart(2, '0');
      const day = String(date).padStart(2, '0');
      setNewEventDate(`${year}-${month}-${day}`);
    } else {
      // Default to today
      const today = new Date();
      const y = today.getFullYear(), m = String(today.getMonth()+1).padStart(2,'0'), d = String(today.getDate()).padStart(2,'0');
      setNewEventDate(`${y}-${m}-${d}`);
    }
    setShowAddEventModal(true);
  };
  
  // Fetch Outlook and Nylas events when month/year changes or accounts load
  useEffect(() => {
    if (apiUrl) {
      checkOutlookSyncStatus();
      // Only fetch Nylas events if we have accounts loaded
      if (nylasAccounts.length > 0) {
        fetchNylasEvents();
      }
    }
  }, [currentMonth, currentYear, apiUrl, nylasAccounts]);

  // ----- Real-time refresh: poll for newly synced events every 30 seconds
  // so add/edit/delete in Outlook lands here within ~30s without a manual
  // refresh. Pauses while the tab is hidden to save bandwidth/CPU.
  useEffect(() => {
    if (!apiUrl) return undefined;
    let intervalId = null;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      // Lightweight: only re-fetch the current month + the sync-status row
      fetchOutlookEvents();
      if (nylasAccounts.length > 0) fetchNylasEvents();
    };
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(tick, 30 * 1000);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    // Start immediately if visible, pause otherwise
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        // Fire one immediate refresh on tab refocus, then resume polling
        tick();
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
  }, [apiUrl, currentMonth, currentYear, nylasAccounts.length]);

  const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 
                  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  // Calculate the starting day of week dynamically for current month/year
  const getMonthStartDay = (month, year) => {
    return new Date(year, month, 1).getDay();
  };
  const monthStartDay = getMonthStartDay(currentMonth, currentYear);
  
  // Also calculate days in month dynamically (handles leap years)
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };
  const currentDaysInMonth = getDaysInMonth(currentMonth, currentYear);

  // Business calendar events by category (with full details for modal)
  // Business calendar events - empty by default, populated from connected accounts
  const businessEvents = {
    all: [],
    deals: [],
    meetings: [],
    deadlines: [],
  };

  // Personal calendar events - empty by default, populated from connected accounts
  const personalEvents = {
    all: [],
    fitness: [],
    health: [],
    social: [],
    family: [],
  };

  const currentEvents = activeCalendar === 'business' ? businessEvents : personalEvents;
  const businessCategories = ['all', 'deals', 'meetings', 'deadlines'];
  const personalCategories = ['all', 'fitness', 'health', 'social', 'family'];
  const availableCategories = activeCalendar === 'business' ? businessCategories : personalCategories;

  const handleEventClick = (event, day) => {
    setSelectedEvent({...event, day, month: months[currentMonth], year: currentYear});
    setShowEventModal(true);
  };

  const handleCalendarSwitch = (calendarType) => {
    setActiveCalendar(calendarType);
    if (calendarType === 'personal' && !personalCategories.includes(activeCategory)) {
      setActiveCategory('all');
    } else if (calendarType === 'business' && !businessCategories.includes(activeCategory)) {
      setActiveCategory('all');
    }
  };

  // Use custom calendar categories (loaded from localStorage or defaults)
  const outlookCategories = customCalendarCategories;
  
  // Category management functions
  const addCalendarCategory = () => {
    if (!newCategoryName.trim()) return;
    
    const categoryId = newCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    // Check if category already exists
    if (customCalendarCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const newCategory = {
      id: categoryId,
      label: newCategoryName,
      color: newCategoryColor,
      icon: newCategoryIcon
    };
    
    const updatedCategories = [...customCalendarCategories, newCategory];
    setCustomCalendarCategories(updatedCategories);
    saveCategoriesToDatabase(updatedCategories);
    setNewCategoryName('');
    setNewCategoryIcon('📅');
    setNewCategoryColor('blue');
    toast.success(`Added "${newCategoryName}" category`);
  };
  
  const updateCalendarCategory = (oldId, newName, newIcon, newColor) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    const updatedCategories = customCalendarCategories.map(c => {
      if (c.id === oldId) {
        return { ...c, id: newId, label: newName, icon: newIcon || c.icon, color: newColor || c.color };
      }
      return c;
    });
    
    setCustomCalendarCategories(updatedCategories);
    saveCategoriesToDatabase(updatedCategories);
    if (activeCategory === oldId) setActiveCategory(newId);
    setEditingCategory(null);
    setEditCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteCalendarCategory = (categoryId) => {
    // Protected categories that cannot be deleted
    const protectedCategories = ['all', 'meeting', 'work', 'other'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    const updatedCategories = customCalendarCategories.filter(c => c.id !== categoryId);
    setCustomCalendarCategories(updatedCategories);
    saveCategoriesToDatabase(updatedCategories);
    if (activeCategory === categoryId) setActiveCategory('all');
    toast.success('Category deleted');
  };
  
  // Get category counts from Outlook events
  // Normalize category keys: handles plural→singular aliases so user tabs
  // renamed (e.g., "Meetings" with id "meetings") still match auto-categorized
  // events stored with singular ids (e.g., "meeting") from the backend.
  const CATEGORY_ALIASES = {
    meetings: 'meeting',
    deals: 'work',
    deadlines: 'deadline',
    fitness: 'health',
    vacation: 'travel',
    birthdays: 'birthday',
    appointments: 'health',
    workout: 'sports',
    workouts: 'sports',
  };
  const normalizeCat = (c) => {
    const x = (c || 'other').toString().toLowerCase().trim();
    return CATEGORY_ALIASES[x] || x;
  };

  const getCategoryCounts = () => {
    const counts = {};
    outlookEvents.forEach(event => {
      const cat = normalizeCat(event.category || event.type || 'other');
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  };
  const categoryCounts = getCategoryCounts();
  
  // Show ALL categories (not just ones with events)
  const availableOutlookCategories = outlookCategories;

  const getEventsForDay = (day) => {
    let events = [];
    
    // Helper function to parse time for sorting
    const parseTimeForSort = (event) => {
      const timeStr = event.time || event.start_time || '';
      if (!timeStr) return 9999; // Events without time go to end
      
      // Handle Unix timestamp
      if (typeof timeStr === 'number') {
        const date = new Date(timeStr * 1000);
        return date.getHours() * 60 + date.getMinutes();
      }
      
      // Handle HH:MM format (with optional AM/PM)
      const match = String(timeStr).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const ampm = match[3];
        
        // Convert 12-hour to 24-hour if AM/PM present
        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
          if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }
        
        return hours * 60 + minutes;
      }
      return 9999;
    };
    
    // Format time from Unix timestamp or ISO string
    const formatEventTime = (timestamp) => {
      if (!timestamp) return '';
      try {
        const date = typeof timestamp === 'number'
          ? new Date(timestamp * 1000)
          : new Date(timestamp);
        if (isNaN(date.getTime())) return String(timestamp);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch {
        return String(timestamp);
      }
    };
    
    // If Outlook is synced and showing, use Outlook events
    if (outlookSyncStatus.synced && showOutlookEvents) {
      // Only include Outlook events if 'all' or 'outlook' tab is selected
      if (activeAccountTab === 'all' || activeAccountTab === 'outlook') {
        // Get Outlook events for this day, filtered by category
        const outlookDayEvents = outlookEvents
          .filter(event => {
            if (event.day !== day) return false;
            if (activeCategory !== 'all') {
              const eventCat = normalizeCat(event.category || event.type || 'other');
              if (eventCat !== normalizeCat(activeCategory)) return false;
            }
            return true;
          })
          .map(event => ({
            ...event,
            // Events already have `time`, `start_time`, `end_time`, `all_day`
            // populated at fetch time; just normalize the display name.
            name: event.subject || event.title || event.name || 'Outlook Event',
            source: 'outlook',
          }));
        events = [...events, ...outlookDayEvents];
      }
    }
    
    // Add Nylas events if showing
    if (showNylasEvents && nylasEvents.length > 0) {
      // Only include Nylas events if 'all' tab or specific Nylas account tab is selected
      if (activeAccountTab === 'all' || (activeAccountTab !== 'outlook' && nylasAccounts.some(a => a.email === activeAccountTab))) {
        const nylasDayEvents = nylasEvents
          .filter(event => {
            if (event.day !== day) return false;
            // Filter by account tab if not 'all' and not 'outlook'
            if (activeAccountTab !== 'all' && activeAccountTab !== 'outlook' && event.account_email !== activeAccountTab) {
              return false;
            }
            if (activeCategory !== 'all') {
              // Map Nylas events to categories based on title/description
              const eventCat = normalizeCat(event.category || 'other');
              if (eventCat !== normalizeCat(activeCategory)) return false;
            }
            return true;
          })
          .map(event => ({
            ...event,
            name: event.title || event.name || 'Calendar Event',
            time: formatEventTime(event.start_time),
            source: 'nylas',
            color: 'purple' // Distinguish Nylas events with purple
          }));
        events = [...events, ...nylasDayEvents];
      }
    }
    
    // Add Local Calendar events
    if (activeAccountTab === 'all' || activeAccountTab === 'local-work' || activeAccountTab === 'local-personal') {
      const localDayEvents = localEvents
        .filter(event => {
          // Filter by calendar type (Work/Personal) if specific tab selected
          const eventCalType = event.calendar_type || 'work'; // Default to 'work' for legacy events
          if (activeAccountTab === 'local-work' && eventCalType !== 'work') return false;
          if (activeAccountTab === 'local-personal' && eventCalType !== 'personal') return false;
          
          // Derive the local calendar date from start_time so the square always matches
          // the local time the user intended — regardless of what was stored in day/month/year.
          let evDay, evMonth, evYear;
          if (event.start_time) {
            const d = new Date(event.start_time);
            evDay   = d.getDate();
            evMonth = d.getMonth();
            evYear  = d.getFullYear();
          } else {
            evDay   = event.day;
            evMonth = event.month;
            evYear  = event.year;
          }

          // ── Recurring events: also match on weekly / monthly cadence ──
          const recurrence = event.recurrence || 'none';
          const cellDate = new Date(currentYear, currentMonth, day);
          const evDate   = new Date(evYear, evMonth, evDay);
          const isOnOrAfterStart = cellDate.getTime() >= evDate.getTime();

          let matches = (evDay === day && evMonth === currentMonth && evYear === currentYear);

          if (!matches && recurrence === 'weekly' && isOnOrAfterStart) {
            // Repeat on the same weekday as the original event
            matches = cellDate.getDay() === evDate.getDay();
          }
          if (!matches && recurrence === 'monthly' && isOnOrAfterStart) {
            // Repeat on the same day-of-month as the original event
            matches = day === evDay;
          }

          if (!matches) return false;

          // Filter by category if not 'all'
          if (activeCategory !== 'all') {
            const eventCat = normalizeCat(event.category || 'other');
            if (eventCat !== normalizeCat(activeCategory)) return false;
          }
          return true;
        })
        .map(event => ({
          ...event,
          name: event.title || 'Local Event',
          time: formatEventTime(event.start_time),
          source: 'local',
          color: event.color || getCategoryColor(event.category || 'other') // Use event's category color
        }));
      events = [...events, ...localDayEvents];
    }
    
    // If no synced events, use static events as fallback
    if (events.length === 0 && !outlookSyncStatus.synced && nylasEvents.length === 0 && localEvents.length === 0) {
      const staticEvents = currentEvents[activeCategory] || currentEvents.all || [];
      events = staticEvents
        .filter(event => event.day === day)
        .map(event => ({
          ...event,
          source: 'static'
        }));
    }
    
    // Deduplicate events by name and time to prevent duplicates
    const uniqueEvents = events.reduce((acc, event) => {
      const eventKey = `${event.name}-${event.time || ''}-${event.start_time || ''}`.toLowerCase();
      if (!acc.some(e => `${e.name}-${e.time || ''}-${e.start_time || ''}`.toLowerCase() === eventKey)) {
        acc.push(event);
      }
      return acc;
    }, []);
    
    // Sort events by time (earliest first)
    return uniqueEvents.sort((a, b) => parseTimeForSort(a) - parseTimeForSort(b));
  };

  // Handler to show all events for a day
  const handleShowDayEvents = (day, events) => {
    setSelectedDayEvents({ day, events });
    setShowDayEventsModal(true);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'all': 'gray',
      'meeting': 'blue',
      'work': 'blue',
      'birthday': 'pink',
      'health': 'red',
      'sports': 'orange',
      'religious': 'purple',
      'travel': 'cyan',
      'home': 'green',
      'social': 'yellow',
      'education': 'indigo',
      'networking': 'teal',
      'other': 'gray'
    };
    return colors[category] || 'gray';
  };
  
  const getColorClasses = (color) => {
    const colorMap = {
      'blue': 'bg-blue-600 border-blue-400',
      'red': 'bg-red-600 border-red-400',
      'orange': 'bg-orange-500 border-orange-400',
      'pink': 'bg-pink-500 border-pink-400',
      'purple': 'bg-purple-600 border-purple-400',
      'cyan': 'bg-cyan-500 border-cyan-400',
      'green': 'bg-green-600 border-green-400',
      'yellow': 'bg-yellow-500 border-yellow-400',
      'indigo': 'bg-indigo-600 border-indigo-400',
      'teal': 'bg-teal-500 border-teal-400',
      'emerald': 'bg-emerald-500 border-emerald-400',
      'gray': 'bg-gray-600 border-gray-400'
    };
    return colorMap[color] || 'bg-gray-600 border-gray-400';
  };

  const getCategoryLabel = (category) => {
    const cat = outlookCategories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-hidden flex flex-col">
      {/* Page Header - Calendar title at very top */}
      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4">Calendar</h1>

      {/* Calendar Settings Container - Sync, Account Tabs (Collapsible) */}
      <div className="mb-3 md:mb-6 bg-black border border-gray-400 rounded-xl flex-shrink-0 overflow-hidden">
        {/* Collapsible Header */}
        <button
          onClick={() => setIsAccountsCollapsed(!isAccountsCollapsed)}
          className="w-full p-3 md:p-4 flex items-center justify-between hover:bg-gray-900/50 transition-colors"
          data-testid="calendar-accounts-collapse-toggle"
        >
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-purple-400" />
            <span className="text-sm md:text-base font-semibold text-white">Connected Accounts</span>
            {(nylasAccounts.length > 0 || outlookSyncStatus.synced) && (
              <span className="text-xs text-gray-500">
                ({nylasAccounts.length + (outlookSyncStatus.synced ? 1 : 0)} connected)
              </span>
            )}
          </div>
          <ChevronDown 
            size={18} 
            className={`text-gray-400 transition-transform duration-200 ${isAccountsCollapsed ? '-rotate-90' : ''}`} 
          />
        </button>
        
        {/* Collapsible Content */}
        <div className={`transition-all duration-200 ease-in-out ${isAccountsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'}`}>
          <div className="px-3 md:px-4 pb-3 md:pb-4">
            {outlookSyncStatus.synced && outlookSyncStatus.last_sync && (
              <div className="mb-2 md:mb-3 text-[10px] md:text-sm text-gray-400 flex items-center flex-wrap gap-2">
                <Cloud size={12} className="inline" />
                <span>
                  Outlook: Last sync {new Date(outlookSyncStatus.last_sync).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })} CT
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowOutlookCalendarModal(true); }}
                  className="ml-auto px-2 py-1 text-[10px] md:text-xs font-medium rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 flex items-center gap-1"
                  title="Generate or manage the permanent Mac sync key"
                  data-testid="open-mac-sync-settings-btn"
                >
                  <KeyRound size={12} />
                  Mac Sync Settings
                </button>
              </div>
            )}
            
            {/* Nylas Sync Status */}
            {nylasAccounts.length > 0 && (
              <div className="mb-2 md:mb-3 text-[10px] md:text-sm text-gray-400 flex items-center gap-2">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  Connected: {nylasAccounts.map(a => a.email).join(', ')} ({nylasEvents.length} events)
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); fetchNylasEvents(); }}
                  disabled={syncingNylas}
                  className="p-1 rounded hover:bg-gray-700 transition-colors"
                  title="Refresh Nylas events"
                >
                  <RefreshCw size={12} className={syncingNylas ? 'animate-spin text-purple-400' : 'text-gray-400'} />
                </button>
              </div>
            )}

            {/* Calendar Account Tabs - Always show for Work/Personal local calendars */}
            <div className="mb-3 md:mb-4" data-testid="calendar-account-tabs">
              <div className="flex gap-2 flex-wrap overflow-x-auto pb-1">
              {/* All Tab */}
              <button
                onClick={() => setActiveAccountTab('all')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeAccountTab === 'all'
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="calendar-tab-all"
              >
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                All Calendars
                <span className={`text-[10px] md:text-xs ${activeAccountTab === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({localEvents.length + nylasEvents.length})
                </span>
              </button>
              
              {/* Local Calendar Tab - Work */}
              <button
                onClick={() => setActiveAccountTab('local-work')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeAccountTab === 'local-work'
                    ? 'bg-blue-600 text-white border-2 border-blue-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="calendar-tab-local-work"
              >
                <span className="text-sm">💼</span>
                <span>Work</span>
                <span className={`text-[10px] md:text-xs ${activeAccountTab === 'local-work' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({localEvents.filter(e => (e.calendar_type || 'work') === 'work').length})
                </span>
              </button>
              
              {/* Local Calendar Tab - Personal */}
              <button
                onClick={() => setActiveAccountTab('local-personal')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeAccountTab === 'local-personal'
                    ? 'bg-green-600 text-white border-2 border-green-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="calendar-tab-local-personal"
              >
                <span className="text-sm">🏠</span>
                <span>Personal</span>
                <span className={`text-[10px] md:text-xs ${activeAccountTab === 'local-personal' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({localEvents.filter(e => e.calendar_type === 'personal').length})
                </span>
              </button>
              
              {/* Individual Nylas Account Tabs */}
              {nylasAccounts.map((account) => {
                const accountEventCount = nylasEvents.filter(e => e.account_email === account.email).length;
                const isActive = activeAccountTab === account.email;
                
                return (
                  <div key={account.email} className="relative group flex items-center">
                    <button
                      onClick={() => setActiveAccountTab(account.email)}
                      className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                        isActive
                          ? 'bg-purple-600 text-white border-2 border-purple-400'
                          : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid={`calendar-tab-${account.email}`}
                      title={account.email}
                    >
                      {/* Provider Icon */}
                      {account.provider === 'google' ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      ) : account.provider === 'microsoft' || account.provider === 'outlook' ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                          <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                        </svg>
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-[10px] text-white font-bold">
                          {account.email.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="max-w-[120px] md:max-w-[180px] truncate">
                        {account.account_name || account.email.split('@')[0]}
                      </span>
                      <span className={`text-[10px] md:text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                        ({accountEventCount})
                      </span>
                    </button>
                    {/* Delete button - appears on hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCalendar(account);
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title={`Disconnect ${account.email}`}
                      data-testid={`delete-calendar-${account.email}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
              
              {/* Add Calendar Button */}
              <button
                onClick={handleConnectCalendar}
                className="px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap bg-gray-800 border border-dashed border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white hover:border-gray-500"
                title="Connect new calendar via Nylas"
                data-testid="add-calendar-btn"
              >
                <Plus size={14} />
                <span className="hidden sm:inline">Add Calendar</span>
              </button>
                </div>
              </div>
            
            {/* No Calendar Connected Message */}
            {!outlookSyncStatus.synced && nylasAccounts.length === 0 && (
              <div className="flex items-center gap-2 md:gap-3 text-[10px] md:text-sm text-gray-400">
                <Cloud size={14} className="text-gray-500 flex-shrink-0" />
                <span>No calendar connected • Connect email in Communications to auto-sync calendars</span>
              </div>
            )}
          </div>
        </div>

        {/* Delete Calendar Confirmation Modal */}
        {showDeleteCalendarConfirm && calendarToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md mx-4">
              <h3 className="text-xl font-bold text-white mb-2">Disconnect Calendar?</h3>
              <p className="text-gray-400 mb-4">
                Are you sure you want to disconnect <strong className="text-white">{calendarToDelete.account_name || calendarToDelete.email}</strong>?
                <span className="block mt-2 text-sm">This will remove all calendar events from this account.</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteCalendarConfirm(false);
                    setCalendarToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  disabled={deletingCalendar}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCalendar}
                  disabled={deletingCalendar}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {deletingCalendar ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Disconnect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Calendar Area - Vertical Categories on Left, Calendar on Right */}
      <div className="flex gap-3 flex-1 overflow-hidden">
        {/* Left Sidebar - Vertical Category Tabs */}
        {(outlookSyncStatus.synced || nylasEvents.length > 0 || localEvents.length > 0 || activeAccountTab === 'local') && (
          <div className="hidden md:flex flex-col gap-1.5 w-36 flex-shrink-0">
            <span className="text-xs font-semibold text-gray-500 mb-1 px-2">Categories</span>
            {availableOutlookCategories.map((cat) => {
              const outlookCount = cat.id === 'all' ? outlookEvents.length : (categoryCounts[normalizeCat(cat.id)] || 0);
              const nylasCount = cat.id === 'all' ? nylasEvents.length : 0;
              const localCount = cat.id === 'all' ? localEvents.length : localEvents.filter(e => normalizeCat(e.category) === normalizeCat(cat.id)).length;
              const count = outlookCount + nylasCount + localCount;
              const isActive = activeCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`calendar-category-${cat.id}`}
                  className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 w-full text-left ${
                    isActive
                      ? `${getColorClasses(cat.color)} text-white border-2`
                      : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {cat.icon && <span className="text-sm">{cat.icon}</span>}
                  {!cat.icon && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getColorClasses(cat.color).split(' ')[0]}`}></span>}
                  <span className="truncate flex-1">{cat.label}</span>
                  <span className={`text-[10px] ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{count}</span>
                </button>
              );
            })}
            
            {/* Edit Categories Button */}
            <button
              onClick={() => setShowCategoryModal(true)}
              data-testid="edit-calendar-categories-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-2"
              title="Edit Categories"
            >
              <Pencil size={12} />
              <span>Edit Categories</span>
            </button>

            {/* Manage Keywords Button — opens keyword editor + re-scan */}
            <button
              onClick={() => setShowKeywordsModal(true)}
              data-testid="manage-keywords-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-2"
              title="Manage Keywords & Re-scan Events"
            >
              <Search size={12} />
              <span>Keywords & Re-scan</span>
            </button>

            {/* Claim Legacy Events — one-time backfill of user_id on orphan events */}
            <button
              onClick={claimOrphanEvents}
              disabled={claimingOrphans}
              data-testid="claim-orphan-events-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-2 disabled:opacity-50"
              title="Assign your user_id to legacy events that don't have one (one-time fix). Run if event editing fails with 'Event not found'."
            >
              <Check size={12} />
              <span>{claimingOrphans ? 'Claiming…' : 'Claim Legacy Events'}</span>
            </button>

            {/* Extract Meeting Links — backfill Teams/Zoom URLs from event body/location */}
            <button
              onClick={extractMeetingLinks}
              disabled={extractingLinks}
              data-testid="extract-meeting-links-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/30 hover:text-emerald-200 flex items-center gap-2 disabled:opacity-50"
              title="Scan stored events for Teams/Zoom/Meet URLs in their body or location and attach them. Use once after enabling sync to pull links into older events."
            >
              <Video size={12} />
              <span>{extractingLinks ? 'Extracting…' : 'Extract Meeting Links'}</span>
            </button>

            {/* Dedupe Events — clean up duplicate rows after rename-in-Outlook */}
            <button
              onClick={dedupeEvents}
              disabled={deduping}
              data-testid="dedupe-events-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-amber-700/60 text-amber-300 hover:bg-amber-900/30 hover:text-amber-200 flex items-center gap-2 disabled:opacity-50"
              title="Remove duplicate rows left behind when an event was renamed in Outlook. Safe — keeps the most recently synced copy."
            >
              <Trash2 size={12} />
              <span>{deduping ? 'Cleaning…' : 'Remove Duplicates'}</span>
            </button>
          </div>
        )}

        {/* Right column: header above the calendar widget */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Shared header — view toggles + Add Event work from ANY view */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 md:mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setActiveView('today')}
              data-testid="view-today-btn"
              className={`px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors ${
                activeView === 'today' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setActiveView('week')}
              data-testid="view-week-btn"
              className={`px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors ${
                activeView === 'week' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setActiveView('month')}
              data-testid="view-month-btn"
              className={`px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors ${
                activeView === 'month' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setActiveView('year')}
              data-testid="view-year-btn"
              className={`px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-semibold transition-colors ${
                activeView === 'year' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Year
            </button>
          </div>
          <button
            onClick={() => openAddEventModal()}
            data-testid="add-event-btn"
            className="px-3 md:px-4 py-1 md:py-1.5 bg-white hover:bg-gray-200 text-black rounded-lg text-xs md:text-sm font-semibold transition-colors flex items-center gap-1.5"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Add Event</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* Calendar Widget */}
        {activeView === 'today' ? (
          <TodayCalendarView
            outlookEvents={outlookEvents}
            nylasEvents={nylasEvents}
            localEvents={localEvents}
            onEventClick={(ev) => {
              const d = new Date(ev.start_time || ev.start || Date.now());
              if (!isNaN(d.getTime())) {
                setCurrentMonth(d.getMonth());
                setCurrentYear(d.getFullYear());
                handleEventClick(ev, d.getDate());
              } else {
                handleEventClick(ev);
              }
            }}
          />
        ) : activeView === 'year' ? (
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex-1 overflow-hidden">
            <YearlyCalendarView
              userId={userId}
              localEvents={localEvents}
              nylasEvents={nylasEvents}
              outlookEvents={outlookEvents}
              onEventClick={(event, day, month, year) => {
                setCurrentMonth(month);
                setCurrentYear(year);
                handleEventClick(event, day);
              }}
            />
          </div>
        ) : activeView === 'week' ? (
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex-1 overflow-hidden">
            <WeeklyCalendarView
              userId={userId}
              localEvents={localEvents}
              nylasEvents={nylasEvents}
              outlookEvents={outlookEvents}
              nylasAccounts={nylasAccounts}
              onEventClick={handleEventClick}
              onAddEvent={() => setShowAddEventModal(true)}
              onEventDrop={handleWeekEventDrop}
            />
          </div>
        ) : (
        <div className="bg-black border border-gray-400 rounded-xl p-2 md:p-6 mb-3 md:mb-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex-1 overflow-auto">
          {/* Month/Year Title with Navigation */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-white">
              {months[currentMonth]} {currentYear}
            </h2>
            <div className="flex items-center gap-1">
              <button 
                onClick={handlePrevMonth}
                className="p-1 md:p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button 
                onClick={handleNextMonth}
                className="p-1 md:p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5 md:gap-2">
            {/* Day Headers */}
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
              <div key={day} className="text-center text-[8px] md:text-xs font-bold text-gray-400 py-1 md:py-2 border-b border-gray-400/20">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}

            {/* Calendar Days */}
            {/* Empty cells for days before month starts */}
            {[...Array(monthStartDay)].map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[60px] md:min-h-[100px]"></div>
            ))}

            {/* Month Days */}
            {[...Array(currentDaysInMonth)].map((_, i) => {
              const day = i + 1;
              const today = new Date();
              const isToday = currentMonth === today.getMonth() && day === today.getDate() && currentYear === today.getFullYear();
              const dayEvents = getEventsForDay(day);
              
              return (
                <div
                  key={day}
                  onClick={() => {
                    if (dayEvents.length > 0) {
                      handleShowDayEvents(day, dayEvents);
                    } else {
                      // No events — open add event modal with this date pre-filled
                      const y = currentYear;
                      const m = String(currentMonth + 1).padStart(2, '0');
                      const d = String(day).padStart(2, '0');
                      setNewEventDate(`${y}-${m}-${d}`);
                      setShowAddEventModal(true);
                    }
                  }}
                  onDragOver={(e) => handleDayDragOver(e, day)}
                  onDragLeave={handleDayDragLeave}
                  onDrop={(e) => handleDayDrop(e, day)}
                  className={`min-h-[60px] md:min-h-[100px] rounded-lg p-1 md:p-2 hover:bg-white/10 transition-colors cursor-pointer ${
                    isToday ? 'bg-white/5 border-2 border-white' : 'bg-black border border-gray-400/20'
                  } ${dragOverTarget === day ? 'ring-2 ring-blue-400 bg-blue-900/20' : ''}`}
                >
                  <div className={`text-xs md:text-sm font-semibold mb-0.5 md:mb-1 ${isToday ? 'text-white' : 'text-gray-300'}`}>
                    {day}
                  </div>
                  {dayEvents.length > 0 && (
                    <div className="flex flex-col gap-0.5 md:gap-1">
                      {dayEvents.slice(0, 2).map((event, idx) => (
                        <div
                          key={idx}
                          draggable={event.source === 'local'}
                          onDragStart={(e) => handleEventDragStart(e, event)}
                          onDragEnd={handleEventDragEnd}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event, day);
                          }}
                          className={`px-1 md:px-1.5 py-0.5 md:py-1 rounded text-[9px] md:text-xs font-medium truncate cursor-pointer hover:opacity-80 transition-opacity relative ${
                            event.color ? getColorClasses(event.color) : getColorClasses(getCategoryColor(event.category || event.type || 'other'))
                          } text-white ${event.source === 'local' ? 'cursor-grab active:cursor-grabbing' : ''}`}
                          title={`${event.name} (${event.source === 'local' ? 'Local - drag to move' : getCategoryLabel(event.category || event.type || 'other')})`}
                        >
                          <span className="hidden md:inline">{event.name}</span>
                          <span className="md:hidden">{event.name.length > 8 ? event.name.substring(0, 8) + '...' : event.name}</span>
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowDayEvents(day, dayEvents);
                          }}
                          className="text-[8px] md:text-xs text-blue-400 hover:text-blue-300 text-center cursor-pointer hover:underline transition-colors"
                        >
                          +{dayEvents.length - 2} more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
        )}
        </div>
      </div>

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => { setShowEventModal(false); setIsEditingEvent(false); }}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-2xl w-full p-4 md:p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              onClick={() => { setShowEventModal(false); setIsEditingEvent(false); }}
              className="absolute top-2 md:top-4 right-2 md:right-4 text-white hover:bg-white hover:text-black p-1.5 md:p-2 rounded transition-colors"
            >
              <X size={20} className="md:hidden" />
              <X size={24} className="hidden md:block" />
            </button>

            {/* Delete Button — only for synced events (Outlook/Calendar.app/web).
                Local-only events have their own delete UI elsewhere. */}
            {selectedEvent && (selectedEvent.source === 'outlook' || selectedEvent.source === 'calendar' || selectedEvent.source === 'web') && (
              <button
                onClick={() => deleteSyncedEvent(selectedEvent.id)}
                disabled={deletingEvent}
                title="Delete event (also removes from Outlook on next sync)"
                data-testid="delete-synced-event-btn"
                className="absolute top-2 md:top-4 right-14 md:right-16 px-2.5 py-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded text-xs font-medium flex items-center gap-1 disabled:opacity-50"
              >
                <Trash2 size={14} />
                <span className="hidden md:inline">Delete</span>
              </button>
            )}

            {!isEditingEvent ? (
              /* View Mode */
              <>
                {/* Event Header */}
                <div className="mb-4 md:mb-6 pr-8">
                  <div className={`inline-block px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold mb-2 md:mb-3 ${
                    selectedEvent.source === 'local' ? 'bg-green-600 text-white' :
                    selectedEvent.source === 'nylas' ? 'bg-purple-600 text-white' :
                    selectedEvent.source === 'outlook' ? 'bg-blue-600 text-white' :
                    selectedEvent.color === 'green' ? 'bg-green-600 text-white' :
                    selectedEvent.color === 'blue' ? 'bg-blue-600 text-white' :
                    selectedEvent.color === 'yellow' ? 'bg-yellow-600 text-black' :
                    selectedEvent.color === 'orange' ? 'bg-orange-600 text-white' :
                    selectedEvent.color === 'red' ? 'bg-red-600 text-white' :
                    selectedEvent.color === 'pink' ? 'bg-pink-600 text-white' :
                    selectedEvent.color === 'purple' ? 'bg-purple-600 text-white' :
                    'bg-gray-600 text-white'
                  }`}>
                    {selectedEvent.source === 'local' ? 'LOCAL' : 
                     selectedEvent.source === 'nylas' ? 'SYNCED' :
                     selectedEvent.source === 'outlook' ? 'OUTLOOK' :
                     (selectedEvent.type || selectedEvent.category || 'EVENT').toUpperCase()}
                  </div>
                  {editingField === 'subject' ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        type="text"
                        value={editFieldValue}
                        onChange={(e) => setEditFieldValue(e.target.value)}
                        className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-xl md:text-3xl font-bold focus:border-blue-500 focus:outline-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEventField(selectedEvent.id, 'subject', editFieldValue);
                          if (e.key === 'Escape') setEditingField(null);
                        }}
                        data-testid="event-subject-edit-input"
                      />
                      <button
                        onClick={() => saveEventField(selectedEvent.id, 'subject', editFieldValue)}
                        disabled={savingEventField}
                        className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs"
                      >Save</button>
                      <button
                        onClick={() => setEditingField(null)}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs"
                      >Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <h2 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2">{selectedEvent.title || selectedEvent.name}</h2>
                      {(selectedEvent.source === 'outlook' || selectedEvent.source === 'calendar' || selectedEvent.source === 'web') && (
                        <button
                          onClick={() => {
                            setEditFieldValue(selectedEvent.title || selectedEvent.name || '');
                            setEditingField('subject');
                          }}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity p-1"
                          title="Rename event"
                          data-testid="edit-subject-btn"
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-gray-400 text-xs md:text-sm">
                    {selectedEvent.month} {selectedEvent.day}, {selectedEvent.year}
                  </p>
                </div>

                {/* Event Details Grid */}
                <div className="space-y-3 md:space-y-4">
                  {/* Time */}
                  {(selectedEvent.time || selectedEvent.start_time) && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <Clock className="text-blue-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div>
                        <div className="text-xs md:text-sm font-semibold text-gray-400">TIME</div>
                        <div className="text-white text-sm md:text-base">
                          {selectedEvent.all_day ? 'All Day' : 
                           selectedEvent.time ? selectedEvent.time :
                           selectedEvent.start_time ? (() => {
                             try {
                               const date = new Date(selectedEvent.start_time);
                               return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                             } catch { return 'All Day'; }
                           })() : 'All Day'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Location — editable on synced events */}
                  {(selectedEvent.location || selectedEvent.source !== 'local') && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <MapPin className="text-green-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs md:text-sm font-semibold text-gray-400 flex items-center gap-2">
                          LOCATION
                          {selectedEvent.source !== 'local' && editingField !== 'location' && (
                            <button
                              onClick={() => {
                                setEditingField('location');
                                setEditFieldValue(selectedEvent.location || '');
                              }}
                              className="text-[10px] text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline"
                              data-testid="edit-event-location-btn"
                            >
                              edit
                            </button>
                          )}
                        </div>
                        {editingField === 'location' ? (
                          <div className="flex gap-1.5 mt-1">
                            <input
                              type="text"
                              value={editFieldValue}
                              onChange={(e) => setEditFieldValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEventField(selectedEvent.id, 'location', editFieldValue);
                                if (e.key === 'Escape') setEditingField(null);
                              }}
                              autoFocus
                              placeholder="e.g. 123 Main St or 'Microsoft Teams Meeting'"
                              className="flex-1 bg-black border border-white/40 rounded px-2 py-1 text-white text-sm focus:border-white focus:outline-none"
                              data-testid="event-location-input"
                            />
                            <button
                              onClick={() => saveEventField(selectedEvent.id, 'location', editFieldValue)}
                              disabled={savingEventField}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded disabled:opacity-50"
                              data-testid="save-event-location-btn"
                            >
                              {savingEventField ? '…' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingField(null)}
                              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="text-white text-sm md:text-base">
                            {selectedEvent.location || <span className="text-gray-500 italic">No location</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {selectedEvent.description && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <Calendar className="text-purple-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div>
                        <div className="text-xs md:text-sm font-semibold text-gray-400">DESCRIPTION</div>
                        <div className="text-white text-sm md:text-base">{selectedEvent.description}</div>
                      </div>
                    </div>
                  )}

                  {/* Online Meeting Link — editable on synced events */}
                  {(selectedEvent.online_meeting_url || selectedEvent.source !== 'local') && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <Video className="text-blue-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs md:text-sm font-semibold text-gray-400 flex items-center gap-2">
                          ONLINE MEETING
                          {selectedEvent.source !== 'local' && editingField !== 'online_meeting_url' && (
                            <button
                              onClick={() => {
                                setEditingField('online_meeting_url');
                                setEditFieldValue(selectedEvent.online_meeting_url || '');
                              }}
                              className="text-[10px] text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline"
                              data-testid="edit-event-meeting-url-btn"
                            >
                              edit
                            </button>
                          )}
                        </div>
                        {editingField === 'online_meeting_url' ? (
                          <div className="flex flex-col gap-1.5 mt-1">
                            <input
                              type="url"
                              value={editFieldValue}
                              onChange={(e) => setEditFieldValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEventField(selectedEvent.id, 'online_meeting_url', editFieldValue);
                                if (e.key === 'Escape') setEditingField(null);
                              }}
                              autoFocus
                              placeholder="https://teams.microsoft.com/l/meetup-join/..."
                              className="w-full bg-black border border-white/40 rounded px-2 py-1 text-white text-xs focus:border-white focus:outline-none"
                              data-testid="event-meeting-url-input"
                            />
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => saveEventField(selectedEvent.id, 'online_meeting_url', editFieldValue)}
                                disabled={savingEventField}
                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded disabled:opacity-50"
                                data-testid="save-event-meeting-url-btn"
                              >
                                {savingEventField ? '…' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditingField(null)}
                                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded"
                              >
                                Cancel
                              </button>
                              {selectedEvent.online_meeting_url && (
                                <button
                                  onClick={() => saveEventField(selectedEvent.id, 'online_meeting_url', '')}
                                  disabled={savingEventField}
                                  className="ml-auto px-2 py-1 bg-red-600/80 hover:bg-red-600 text-white text-xs rounded disabled:opacity-50"
                                  data-testid="clear-event-meeting-url-btn"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                        ) : selectedEvent.online_meeting_url ? (
                          <>
                            <a
                              href={selectedEvent.online_meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs md:text-sm font-semibold rounded-md transition-colors"
                              data-testid="event-join-meeting-btn"
                            >
                              <Video size={14} />
                              {selectedEvent.online_meeting_url.includes('teams.microsoft.com') || selectedEvent.online_meeting_url.includes('teams.live.com')
                                ? 'Join Teams Meeting'
                                : selectedEvent.online_meeting_url.includes('zoom.us')
                                ? 'Join Zoom Meeting'
                                : selectedEvent.online_meeting_url.includes('meet.google.com')
                                ? 'Join Google Meet'
                                : 'Join Meeting'}
                            </a>
                            <div className="text-[10px] md:text-xs text-gray-500 mt-1 truncate">
                              {selectedEvent.online_meeting_url}
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-500 italic text-sm">No meeting link</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Source Account */}
                  {selectedEvent.account_email && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <Cloud className="text-cyan-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div>
                        <div className="text-xs md:text-sm font-semibold text-gray-400">CALENDAR</div>
                        <div className="text-white text-sm md:text-base">{selectedEvent.account_email}</div>
                      </div>
                    </div>
                  )}

                  {/* Attendees/Participants */}
                  {((selectedEvent.attendees && selectedEvent.attendees.length > 0) || 
                    (selectedEvent.participants && selectedEvent.participants.length > 0)) && (
                    <div className="flex items-start gap-2 md:gap-3">
                      <Users className="text-pink-400 mt-0.5 md:mt-1 flex-shrink-0" size={16} />
                      <div>
                        <div className="text-xs md:text-sm font-semibold text-gray-400">ATTENDEES</div>
                        <div className="text-white text-sm md:text-base">
                          {(selectedEvent.attendees || selectedEvent.participants || []).map((attendee, idx) => (
                            <div key={idx} className="py-0.5 md:py-1">• {typeof attendee === 'string' ? attendee : attendee.email || attendee.name}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/20">
                  {selectedEvent.source === 'local' ? (
                    <div className="flex gap-2 md:gap-3">
                      <button 
                        onClick={() => startEditingEvent(selectedEvent)}
                        className="flex-1 bg-white text-black py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                        data-testid="edit-event-btn"
                      >
                        <Pencil size={16} />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button 
                        onClick={duplicateEvent}
                        className="flex-1 bg-blue-600 border border-blue-500 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        data-testid="duplicate-event-btn"
                      >
                        <Copy size={16} />
                        <span className="hidden sm:inline">Duplicate</span>
                      </button>
                      <button 
                        onClick={deleteEvent}
                        className="flex-1 bg-red-600 border border-red-500 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                        data-testid="delete-event-btn"
                      >
                        <Trash2 size={16} />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Category override for synced events */}
                      <div>
                        <div className="text-xs md:text-sm font-semibold text-gray-400 mb-1.5">CATEGORY</div>
                        <select
                          value={selectedEvent.category || 'other'}
                          onChange={(e) => updateSyncedEventCategory(selectedEvent.id, e.target.value)}
                          disabled={updatingEventCategory}
                          className="w-full bg-black border border-white/40 rounded-lg px-3 py-2 text-white text-sm focus:border-white focus:outline-none disabled:opacity-50"
                          data-testid="event-category-select"
                        >
                          {customCalendarCategories
                            .filter(c => c.id !== 'all')
                            .map(c => (
                              <option key={c.id} value={c.id}>
                                {c.icon} {c.label}
                              </option>
                            ))}
                        </select>
                        {selectedEvent.category_overridden && (
                          <div className="text-[10px] text-emerald-400 mt-1">✓ Category manually set — won't be overwritten by re-scans</div>
                        )}
                      </div>
                      <div className="text-center text-gray-500 text-xs md:text-sm">
                        This event is synced from {selectedEvent.source === 'nylas' ? selectedEvent.account_email : selectedEvent.source}. Edit time/title in your calendar app.
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              /* Edit Mode */
              <>
                <div className="mb-4 md:mb-6 pr-8">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-1">Edit Event</h2>
                </div>

                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-1">Event Title *</label>
                    <input
                      type="text"
                      value={editEventData.title}
                      onChange={(e) => setEditEventData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                      placeholder="Event title"
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-1">Date *</label>
                    <DatePickerPopover
                      value={editEventData.date}
                      onChange={(d) => setEditEventData(prev => ({ ...prev, date: d }))}
                      placeholder="Pick a date"
                      testId="edit-event-date-input"
                    />
                  </div>

                  {/* Time */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-400 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={editEventData.startTime}
                        onChange={(e) => setEditEventData(prev => ({ ...prev, startTime: e.target.value }))}
                        disabled={editEventData.allDay}
                        className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-400 mb-1">End Time</label>
                      <input
                        type="time"
                        value={editEventData.endTime}
                        onChange={(e) => setEditEventData(prev => ({ ...prev, endTime: e.target.value }))}
                        disabled={editEventData.allDay}
                        className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none disabled:opacity-50"
                      />
                    </div>
                  </div>

                  {/* All Day Toggle */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editAllDay"
                      checked={editEventData.allDay}
                      onChange={(e) => setEditEventData(prev => ({ ...prev, allDay: e.target.checked }))}
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="editAllDay" className="text-sm text-gray-300">All Day Event</label>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-1">Location</label>
                    <input
                      type="text"
                      value={editEventData.location}
                      onChange={(e) => setEditEventData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                      placeholder="Event location"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-1">Description</label>
                    <textarea
                      value={editEventData.description}
                      onChange={(e) => setEditEventData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none resize-none"
                      rows={3}
                      placeholder="Event description"
                    />
                  </div>
                  
                  {/* Category Selector (for local events only) */}
                  {selectedEvent?.source === 'local' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-400 mb-1">Category</label>
                      <select
                        value={editEventData.category}
                        onChange={(e) => setEditEventData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                      >
                        {customCalendarCategories.filter(c => c.id !== 'all').map(category => (
                          <option key={category.id} value={category.id}>
                            {category.icon} {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Repeat Selector (for local events only) */}
                  {selectedEvent?.source === 'local' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-400 mb-1">Repeat</label>
                      <select
                        value={editEventData.recurrence || 'none'}
                        onChange={(e) => setEditEventData(prev => ({ ...prev, recurrence: e.target.value }))}
                        className="w-full bg-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                      >
                        <option value="none">Does not repeat</option>
                        <option value="weekly">
                          Every week
                          {editEventData.date ? ` on ${new Date(editEventData.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}` : ''}
                        </option>
                        <option value="monthly">
                          Every month
                          {editEventData.date ? ` on day ${parseInt(editEventData.date.split('-')[2], 10)}` : ''}
                        </option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 md:gap-3 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/20">
                  <button 
                    onClick={saveEditedEvent}
                    className="flex-1 bg-white text-black py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <Check size={16} />
                    Save Changes
                  </button>
                  <button 
                    onClick={cancelEditingEvent}
                    className="flex-1 bg-black border border-gray-400 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Day Events Modal - Shows all events for a selected day */}
      {showDayEventsModal && selectedDayEvents.day && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => setShowDayEventsModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-4 md:p-6 relative max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              onClick={() => setShowDayEventsModal(false)}
              className="absolute top-2 md:top-4 right-2 md:right-4 text-white hover:bg-white hover:text-black p-1.5 md:p-2 rounded transition-colors"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="mb-4 md:mb-6 pr-8">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                {months[currentMonth]} {selectedDayEvents.day}, {currentYear}
              </h2>
              <p className="text-gray-400 text-sm">
                {selectedDayEvents.events.length} event{selectedDayEvents.events.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Events List */}
            <div className="space-y-2 md:space-y-3">
              {selectedDayEvents.events.map((event, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setShowDayEventsModal(false);
                    handleEventClick(event, selectedDayEvents.day);
                  }}
                  className={`p-3 md:p-4 rounded-lg cursor-pointer transition-all hover:scale-[1.02] border-l-4 ${
                    getColorClasses(getCategoryColor(event.category || event.type || 'other')).split(' ')[1]
                  } bg-gray-900 hover:bg-gray-800`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm md:text-base truncate">
                        {event.name}
                      </h3>
                      {(event.time || event.start_time) && (
                        <div className="flex items-center gap-1 text-gray-400 text-xs md:text-sm mt-1">
                          <Clock size={12} />
                          <span>{event.time || event.start_time}</span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-1 text-gray-400 text-xs md:text-sm mt-1">
                          <MapPin size={12} />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-[10px] md:text-xs font-semibold whitespace-nowrap ${
                      getColorClasses(getCategoryColor(event.category || event.type || 'other'))
                    } text-white`}>
                      {getCategoryLabel(event.category || event.type || 'other')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Close Button at Bottom */}
            <div className="mt-4 md:mt-6 pt-4 border-t border-white/20 flex gap-2">
              <button
                onClick={() => {
                  const y = currentYear;
                  const m = String(currentMonth + 1).padStart(2, '0');
                  const d = String(selectedDayEvents.day).padStart(2, '0');
                  setNewEventDate(`${y}-${m}-${d}`);
                  setShowDayEventsModal(false);
                  setShowAddEventModal(true);
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-colors flex items-center justify-center gap-2"
                data-testid="add-event-from-day-modal"
              >
                <Plus size={16} /> Add Event
              </button>
              <button
                onClick={() => setShowDayEventsModal(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white py-2 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Widget Modal */}
      {showWidgetModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowWidgetModal(false)}>
          <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white mokoto-font">
                {widgetModalType === 'thisWeek' && 'This Week - All Events'}
                {widgetModalType === 'criticalDeadlines' && 'All Critical Deadlines'}
                {widgetModalType === 'upcomingEvents' && 'Upcoming Major Events (30 Days)'}
                {widgetModalType === 'teamSchedules' && 'Team Schedules - Detailed View'}
              </h2>
              <button
                onClick={() => setShowWidgetModal(false)}
                className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-black" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {widgetModalType === 'thisWeek' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">All events scheduled for this week (Jan 1-7, 2026)</p>
                  {[
                    { name: 'Empire State Building Closing', date: 'JAN 2', value: '$2.5M', desc: 'Tech Corp lease agreement', type: 'deal' },
                    { name: 'Morning Team Standup', date: 'JAN 3', value: 'N/A', desc: 'Daily sync with all team leads', type: 'meeting' },
                    { name: 'Downtown Listing Launch', date: 'JAN 4', value: '$15M', desc: 'Property at 450 Park Avenue', type: 'marketing' },
                    { name: 'Client Presentation - Riverside Tower', date: 'JAN 5', value: '$8.2M', desc: 'Investor pitch meeting', type: 'meeting' },
                    { name: 'Q1 Revenue Review Meeting', date: 'JAN 6', value: 'N/A', desc: 'Finance team quarterly review', type: 'deadline' },
                    { name: 'Brooklyn Development Site Visit', date: 'JAN 7', value: '$12M', desc: 'Construction progress review', type: 'inspection' }
                  ].map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400">{event.desc}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-blue-400">{event.date}</span>
                          {event.value !== 'N/A' && (
                            <div className="text-lg font-bold text-green-400">{event.value}</div>
                          )}
                        </div>
                      </div>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        event.type === 'deal' ? 'bg-green-500/20 text-green-400' :
                        event.type === 'meeting' ? 'bg-blue-500/20 text-blue-400' :
                        event.type === 'marketing' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {event.type.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {widgetModalType === 'criticalDeadlines' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">All critical deadlines requiring immediate attention</p>
                  {[
                    { name: 'Chelsea Property Contract', days: 2, desc: 'Final contract review deadline', urgency: 'urgent' },
                    { name: 'Brooklyn Warehouse Inspection', days: 5, desc: 'Structural inspection must complete', urgency: 'high' },
                    { name: 'Q4 Financial Report', days: 7, desc: 'Board presentation preparation', urgency: 'medium' },
                    { name: 'Madison Ave Lease Extension', days: 10, desc: 'Tenant renewal decision needed', urgency: 'medium' },
                    { name: 'Zoning Application Submission', days: 12, desc: 'City planning department deadline', urgency: 'high' },
                    { name: 'Property Tax Appeals', days: 14, desc: 'Three properties pending appeals', urgency: 'medium' }
                  ].map((deadline, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`flex-shrink-0 ${
                          deadline.urgency === 'urgent' ? 'text-red-400' :
                          deadline.urgency === 'high' ? 'text-orange-400' :
                          'text-yellow-400'
                        }`} size={24} />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-white">{deadline.name}</h3>
                            <span className={`text-lg font-bold ${
                              deadline.urgency === 'urgent' ? 'text-red-400' :
                              deadline.urgency === 'high' ? 'text-orange-400' :
                              'text-yellow-400'
                            }`}>
                              {deadline.days} DAYS
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mb-2">{deadline.desc}</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            deadline.urgency === 'urgent' ? 'bg-red-500/20 text-red-400' :
                            deadline.urgency === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {deadline.urgency.toUpperCase()} PRIORITY
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {widgetModalType === 'upcomingEvents' && (
                <div className="space-y-6">
                  <p className="text-gray-400 mb-4">Major events scheduled for the next 30 days</p>
                  
                  {/* Week 2 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 2 (Jan 8-14, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'Madison Ave Office Showing', date: 'Jan 10', value: '$6.2M', type: 'VIP Client Tour' },
                        { name: 'Investment Committee Meeting', date: 'Jan 12', value: 'N/A', type: 'Internal Review' },
                        { name: 'Upper East Side Listing', date: 'Jan 14', value: '$9.8M', type: 'New Property' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-blue-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Week 3 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 3 (Jan 15-21, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'Board of Directors Meeting', date: 'Jan 18', value: 'N/A', type: 'Strategic Planning' },
                        { name: 'Tribeca Loft Closing', date: 'Jan 19', value: '$4.5M', type: 'Deal Closing' },
                        { name: 'Annual Team Retreat', date: 'Jan 20-21', value: 'N/A', type: 'Team Building' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-blue-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Week 4 */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Week 4 (Jan 22-28, 2026)</h3>
                    <div className="space-y-3">
                      {[
                        { name: 'SoHo Retail Closing', date: 'Jan 25', value: '$3.8M', type: 'Final Documents' },
                        { name: 'Q1 Forecast Presentation', date: 'Jan 27', value: 'N/A', type: 'Executive Team' },
                        { name: 'New Listing Open House', date: 'Jan 28', value: '$7.2M', type: 'Marketing Event' }
                      ].map((event, idx) => (
                        <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4">
                          <h4 className="text-md font-semibold text-white mb-1">{event.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{event.date}</span>
                            {event.value !== 'N/A' && <span className="text-green-400 font-semibold">{event.value}</span>}
                            <span className="text-purple-400">{event.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {widgetModalType === 'teamSchedules' && (
                <div className="space-y-6">
                  <p className="text-gray-400 mb-4">Detailed schedule overview for all team members this week</p>
                  {[
                    { name: 'Sarah Mitchell', role: 'Senior Broker', events: 12, meetings: 8, deals: 3, showings: 5 },
                    { name: 'John Martinez', role: 'Deal Manager', events: 9, meetings: 6, deals: 5, showings: 2 },
                    { name: 'Alex Rivera', role: 'Property Manager', events: 15, meetings: 10, deals: 2, showings: 7 },
                    { name: 'Emily Chen', role: 'Marketing Director', events: 8, meetings: 5, deals: 1, showings: 3 },
                    { name: 'Michael Wong', role: 'Financial Analyst', events: 11, meetings: 9, deals: 4, showings: 1 }
                  ].map((member, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-6 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                          <p className="text-sm text-gray-400">{member.role}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-white">{member.events}</div>
                          <div className="text-xs text-gray-400">Events This Week</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-400">{member.meetings}</div>
                          <div className="text-xs text-gray-400">Meetings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-400">{member.deals}</div>
                          <div className="text-xs text-gray-400">Deals</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-400">{member.showings}</div>
                          <div className="text-xs text-gray-400">Showings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-400">{member.events - member.meetings - member.deals - member.showings}</div>
                          <div className="text-xs text-gray-400">Other</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowSummaryModal(false)}>
          <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white mokoto-font">
                {summaryModalType === 'total' && 'All Upcoming Events'}
                {summaryModalType === 'critical' && 'Critical Deadlines'}
                {summaryModalType === 'deals' && 'Deal Closings This Month'}
                {summaryModalType === 'value' && 'Active Pipeline ($67M)'}
              </h2>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-black" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {summaryModalType === 'total' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Showing all 89 events scheduled for the next 60 days</p>
                  {businessEvents.all.slice(0, 20).map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400">{event.time}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          event.type === 'deal' ? 'bg-green-500/20 text-green-400' :
                          event.type === 'meeting' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {event.type.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                        {event.value !== 'N/A' && (
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} />
                            {event.value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'critical' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">8 high-priority items requiring immediate attention</p>
                  {businessEvents.all.filter(e => e.type === 'deadline').map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{event.name}</h3>
                          <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-red-400 font-semibold">{event.time}</span>
                            <span className="text-gray-400">{event.location}</span>
                            {event.value !== 'N/A' && (
                              <span className="text-green-400 font-semibold">{event.value}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'deals' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">15 deal closings scheduled this month</p>
                  {businessEvents.all.filter(e => e.type === 'deal').map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-white">{event.name}</h3>
                            <span className="text-2xl font-bold text-green-400">{event.value}</span>
                          </div>
                          <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Jan {event.day}, 2026
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {event.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {event.location}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <Users size={14} className="text-gray-500" />
                            <span className="text-xs text-gray-500">{event.attendees.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {summaryModalType === 'value' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Active pipeline breakdown by deal value</p>
                  <div className="bg-black border border-gray-400 rounded-lg p-6 mb-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Total Pipeline Value</div>
                        <div className="text-4xl font-bold text-purple-400">$67M</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Active Deals</div>
                        <div className="text-4xl font-bold text-white">15</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Average Deal Size</div>
                        <div className="text-2xl font-bold text-white">$4.5M</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Expected Close</div>
                        <div className="text-2xl font-bold text-green-400">Q1 2026</div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-3">High-Value Deals</h3>
                  {businessEvents.all
                    .filter(e => e.type === 'deal')
                    .sort((a, b) => {
                      const valA = parseFloat(a.value.replace(/[$M]/g, ''));
                      const valB = parseFloat(b.value.replace(/[$M]/g, ''));
                      return valB - valA;
                    })
                    .map((event, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-4 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-white">{event.name}</h3>
                        <span className="text-2xl font-bold text-green-400">{event.value}</span>
                      </div>
                      <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Closing: Jan {event.day}, 2026</span>
                        <span>•</span>
                        <span>{event.location}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iCloud Calendar Credentials Modal */}
      {showICloudCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowICloudCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect iCloud Calendar</h3>
                  <p className="text-sm text-gray-400">Sign in to sync your calendar events</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              if (icloudCalEmail && icloudCalPassword) {
                handleICloudCalendarConnect();
              }
            }} className="p-6 space-y-4">
              {calendarError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {calendarError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Apple ID (Email)</label>
                <input
                  type="email"
                  value={icloudCalEmail}
                  onChange={(e) => setIcloudCalEmail(e.target.value)}
                  placeholder="your@icloud.com"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  required
                  data-testid="icloud-cal-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">App-Specific Password</label>
                <input
                  type="password"
                  value={icloudCalPassword}
                  onChange={(e) => setIcloudCalPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  required
                  data-testid="icloud-cal-password-input"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Generate an app-specific password at{' '}
                  <a 
                    href="https://appleid.apple.com/account/manage" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    appleid.apple.com
                  </a>
                  {' '}→ Sign-In & Security → App-Specific Passwords
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowICloudCalendarModal(false);
                    setCalendarError('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={calendarConnecting || !icloudCalEmail || !icloudCalPassword}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="icloud-cal-connect-submit"
                >
                  {calendarConnecting ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Cloud size={18} />
                      Connect
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Google Calendar Connection Modal */}
      {showGoogleCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGoogleCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white rounded-lg">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect Google Calendar</h3>
                  <p className="text-sm text-gray-400">Sync your Google Calendar events</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                    <p>Click the "Connect with Google" button below</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                    <p>Sign in to your Google account</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                    <p>Allow access to your Google Calendar</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                    <p>Your events will sync automatically</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <strong>Note:</strong> Google Calendar integration uses OAuth for secure authentication. Your credentials are never stored on our servers.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowGoogleCalendarModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  window.open('https://calendar.google.com', '_blank');
                  // For now, mark as connected for demo
                  setIsCalendarConnected(true);
                  setConnectedCalendarType('google');
                  localStorage.setItem('calendar_connected_type', 'google');
                  setShowGoogleCalendarModal(false);
                }}
                className="flex-1 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connect with Google
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outlook Calendar Connection Modal */}
      {showOutlookCalendarModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOutlookCalendarModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#0078D4] rounded-lg">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576zM14.637 7.387v13.226H.786c-.226 0-.42-.076-.578-.23C.05 20.23 0 20.036 0 19.81V5.618c0-.226.076-.42.23-.577C.383 4.888.577 4.81.803 4.81h13.834v2.577zm-2.46 5.618c0-.805-.284-1.49-.854-2.056-.57-.566-1.257-.85-2.06-.85-.805 0-1.492.284-2.062.85-.57.566-.855 1.251-.855 2.056 0 .8.285 1.485.855 2.051.57.566 1.257.85 2.062.85.803 0 1.49-.284 2.06-.85.57-.566.854-1.251.854-2.051zm-1.23 0c0 .453-.16.84-.484 1.163-.32.323-.71.484-1.166.484-.457 0-.847-.161-1.17-.484-.324-.322-.486-.71-.486-1.163 0-.453.162-.84.486-1.163.323-.323.713-.484 1.17-.484.456 0 .845.161 1.166.484.323.322.484.71.484 1.163z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect Outlook Calendar</h3>
                  <p className="text-sm text-gray-400">Sync your Outlook calendar events</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* NEW: Permanent Sync Key flow */}
              <PermanentSyncKeyPanel apiBase="https://operatr.me" authToken={authToken} />

              {outlookSyncStatus.synced && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="text-green-400" size={18} />
                  <div>
                    <p className="text-sm text-green-400 font-medium">Outlook Connected</p>
                    <p className="text-xs text-gray-400">
                      Last sync: {new Date(outlookSyncStatus.last_sync).toLocaleString()} • {outlookSyncStatus.event_count} events this month
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowOutlookCalendarModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={connectOutlookCalendar}
                disabled={syncingOutlook}
                className="flex-1 px-4 py-3 bg-[#0078D4] hover:bg-[#106EBE] disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {syncingOutlook ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Syncing...
                  </>
                ) : outlookSyncStatus.synced ? (
                  <>
                    <RefreshCw size={18} />
                    Sync Again
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                    </svg>
                    Connect Outlook
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Event Modal */}
      {showAddEventModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddEventModal(false);
            resetEventForm();
          }}
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus size={20} className="text-purple-400" />
                Add New Event
              </h3>
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  resetEventForm();
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {createEventError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {createEventError}
                </div>
              )}
              
              {/* Event Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Event Title *</label>
                <input
                  type="text"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  placeholder="e.g., Team Meeting"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="event-title-input"
                />
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                <DatePickerPopover
                  value={newEventDate}
                  onChange={setNewEventDate}
                  placeholder="Pick a date"
                  testId="event-date-input"
                />
              </div>
              
              {/* Time Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={newEventStartTime}
                    onChange={(e) => setNewEventStartTime(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    data-testid="event-start-time-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
                  <input
                    type="time"
                    value={newEventEndTime}
                    onChange={(e) => setNewEventEndTime(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={newEventLocation}
                    onChange={(e) => setNewEventLocation(e.target.value)}
                    placeholder="e.g., Conference Room A"
                    className="w-full pl-10 pr-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="event-location-input"
                  />
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={newEventDescription}
                  onChange={(e) => setNewEventDescription(e.target.value)}
                  placeholder="Add event details..."
                  rows={3}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none"
                  data-testid="event-description-input"
                />
              </div>
              
              {/* Category Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  value={newEventCategory}
                  onChange={(e) => setNewEventCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  data-testid="event-category-select"
                >
                  {customCalendarCategories.filter(c => c.id !== 'all').map(category => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Calendar Type Selector (Work/Personal) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Calendar</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setNewEventCalendarType('work')}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                      newEventCalendarType === 'work'
                        ? 'bg-blue-600 text-white border-2 border-blue-400'
                        : 'bg-black border border-gray-600 text-gray-300 hover:bg-gray-800'
                    }`}
                    data-testid="calendar-type-work"
                  >
                    <span>💼</span> Work
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewEventCalendarType('personal')}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                      newEventCalendarType === 'personal'
                        ? 'bg-green-600 text-white border-2 border-green-400'
                        : 'bg-black border border-gray-600 text-gray-300 hover:bg-gray-800'
                    }`}
                    data-testid="calendar-type-personal"
                  >
                    <span>🏠</span> Personal
                  </button>
                </div>
              </div>

              {/* Repeat Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Repeat</label>
                <select
                  value={newEventRepeat}
                  onChange={(e) => setNewEventRepeat(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  data-testid="event-repeat-select"
                >
                  <option value="none">Does not repeat</option>
                  <option value="weekly">
                    Every week
                    {newEventDate ? ` on ${new Date(newEventDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}` : ''}
                  </option>
                  <option value="monthly">
                    Every month
                    {newEventDate ? ` on day ${parseInt(newEventDate.split('-')[2], 10)}` : ''}
                  </option>
                </select>
              </div>
              
              {/* Info Box */}
              {nylasAccounts.length === 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle size={14} className="inline mr-2" />
                    Connect an email account to sync events to your calendar.
                  </p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  resetEventForm();
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createNewEvent}
                disabled={creatingEvent || !newEventTitle.trim() || !newEventDate}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="create-event-btn"
              >
                {creatingEvent ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    Create Event
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Category Management Modal */}
      {showCategoryModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCategoryModal(false)}
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Calendar Categories</h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="close-category-modal-btn"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            {/* Add New Category */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                {/* Icon Selector */}
                <div className="relative">
                  <button
                    type="button"
                    className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                    onClick={(e) => {
                      const dropdown = e.currentTarget.nextElementSibling;
                      dropdown.classList.toggle('hidden');
                    }}
                    data-testid="category-icon-selector"
                  >
                    {newCategoryIcon}
                  </button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                    {calendarCategoryIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={(e) => {
                          setNewCategoryIcon(icon);
                          e.currentTarget.parentElement.classList.add('hidden');
                        }}
                        className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCalendarCategory()}
                  placeholder="New category name..."
                  className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="new-calendar-category-input"
                />
              </div>
              
              {/* Color Selector */}
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {categoryColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewCategoryColor(color)}
                      className={`w-6 h-6 rounded-full ${getColorClasses(color).split(' ')[0]} ${newCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : 'hover:opacity-80'}`}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              <button
                onClick={addCalendarCategory}
                disabled={!newCategoryName.trim()}
                className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                data-testid="add-calendar-category-btn"
              >
                <Plus size={14} />
                Add Category
              </button>
            </div>
            
            {/* Category List */}
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customCalendarCategories.filter(c => c.id !== 'all').map((category) => {
                const protectedCategories = ['meeting', 'work', 'other'];
                const isProtected = protectedCategories.includes(category.id);
                
                return (
                  <div 
                    key={category.id}
                    className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                  >
                    {editingCategory === category.id ? (
                      <>
                        <span className="text-lg">{category.icon}</span>
                        <input
                          type="text"
                          value={editCategoryValue}
                          onChange={(e) => setEditCategoryValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateCalendarCategory(category.id, editCategoryValue, category.icon, category.color);
                            if (e.key === 'Escape') {
                              setEditingCategory(null);
                              setEditCategoryValue('');
                            }
                          }}
                          className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                          autoFocus
                          data-testid={`edit-calendar-category-input-${category.id}`}
                        />
                        <button
                          onClick={() => updateCalendarCategory(category.id, editCategoryValue, category.icon, category.color)}
                          className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                          title="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setEditCategoryValue('');
                          }}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`w-8 h-8 rounded-lg ${getColorClasses(category.color).split(' ')[0]} flex items-center justify-center text-sm`}>
                          {category.icon}
                        </span>
                        <span className="flex-1 text-white text-sm">
                          {category.label}
                          {isProtected && (
                            <span className="ml-2 text-xs text-gray-500">(default)</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setEditingCategory(category.id);
                            setEditCategoryValue(category.label);
                          }}
                          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Edit"
                          data-testid={`edit-calendar-category-btn-${category.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        {!isProtected && (
                          <button
                            onClick={() => deleteCalendarCategory(category.id)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                            data-testid={`delete-calendar-category-btn-${category.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                data-testid="done-calendar-category-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Manage Keywords Modal ---------- */}
      {showKeywordsModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowKeywordsModal(false)}
        >
          <div
            className="bg-gray-900 rounded-2xl w-full max-w-2xl border border-gray-700 shadow-2xl flex flex-col max-h-[88vh]"
            onClick={e => e.stopPropagation()}
            data-testid="keywords-modal"
          >
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Manage Category Keywords</h3>
                <p className="text-xs text-gray-400 mt-0.5">Add custom keywords to auto-tag synced events. User keywords win over defaults.</p>
              </div>
              <button
                onClick={() => setShowKeywordsModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="close-keywords-modal-btn"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {customCalendarCategories.filter(c => c.id !== 'all').map(cat => {
                const kws = categoryKeywords[cat.id] || [];
                const inputVal = keywordInputs[cat.id] || '';
                const addKeyword = () => {
                  const raw = (inputVal || '').trim();
                  if (!raw) return;
                  // Allow comma-separated bulk entry (e.g. "zoom, teams, sync")
                  const incoming = raw
                    .split(/[,\n]/)
                    .map(s => s.trim())
                    .filter(Boolean);
                  if (incoming.length === 0) return;
                  const existingLower = new Set(kws.map(k => k.toLowerCase()));
                  const additions = [];
                  for (const v of incoming) {
                    const lk = v.toLowerCase();
                    if (existingLower.has(lk)) continue;
                    existingLower.add(lk);
                    additions.push(v);
                  }
                  if (additions.length === 0) {
                    setKeywordInputs(prev => ({ ...prev, [cat.id]: '' }));
                    return;
                  }
                  setCategoryKeywords(prev => ({ ...prev, [cat.id]: [...kws, ...additions] }));
                  setKeywordInputs(prev => ({ ...prev, [cat.id]: '' }));
                };
                return (
                  <div key={cat.id} className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{cat.icon}</span>
                        <span className="text-sm font-semibold text-white">{cat.label}</span>
                        <span className="text-[10px] text-gray-500">({kws.length})</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {kws.map(kw => (
                        <span
                          key={kw}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-700/70 text-white text-xs rounded-full"
                        >
                          {kw}
                          <button
                            type="button"
                            onClick={() => setCategoryKeywords(prev => ({ ...prev, [cat.id]: kws.filter(k => k !== kw) }))}
                            className="hover:text-red-400"
                            aria-label={`Remove keyword ${kw}`}
                            data-testid={`remove-keyword-${cat.id}-${kw}`}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      {kws.length === 0 && (
                        <span className="text-[11px] text-gray-500 italic">No custom keywords yet</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inputVal}
                        onChange={(e) => setKeywordInputs(prev => ({ ...prev, [cat.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                        placeholder="Add keyword(s) — comma-separated OK (e.g. zoom, sync, demo)"
                        className="flex-1 bg-black border border-gray-600 rounded-md px-2.5 py-1.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-white"
                        data-testid={`keyword-input-${cat.id}`}
                      />
                      <button
                        type="button"
                        onClick={addKeyword}
                        disabled={!inputVal.trim()}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-md text-xs font-medium"
                        data-testid={`add-keyword-${cat.id}`}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-gray-700 flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={saveCategoryKeywords}
                  disabled={savingKeywords || rescanning}
                  className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  data-testid="save-keywords-btn"
                >
                  {savingKeywords ? 'Saving…' : 'Save Keywords'}
                </button>
                <button
                  onClick={() => rescanAllEvents(false)}
                  disabled={savingKeywords || rescanning}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                  data-testid="rescan-events-btn"
                >
                  {rescanning ? 'Re-scanning…' : 'Save & Re-scan All Events'}
                </button>
              </div>
              <button
                onClick={() => rescanAllEvents(true)}
                disabled={savingKeywords || rescanning}
                className="w-full px-4 py-2 bg-red-900/40 hover:bg-red-800/60 border border-red-700/50 text-red-200 rounded-lg text-xs font-medium disabled:opacity-50"
                data-testid="force-rescan-events-btn"
                title="Wipes all your manual category overrides and re-categorizes every event from scratch."
              >
                ⚠ Force re-scan (also reset my manual overrides)
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RealEstateManagingPartnerCalendar;
